import React, { useState, useEffect, useMemo, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import xrplToService from '../services/XrplToService';
import { theme, darkTheme } from '../styles/GlobalStyle';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { ThemeContext } from '../contexts/ThemeContext';

// Animation keyframes
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(0deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const pop = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  70% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const TokenFeedContainer = styled.div`
  background: ${props => props.theme === darkTheme ? 
    'rgba(19, 47, 76, 0.95)' : 
    'rgba(255, 255, 255, 0.95)'};
  border-radius: 25px;
  box-shadow: 0 15px 35px rgba(63, 140, 255, 0.2);
  border: 4px solid ${props => props.theme.borderLight};
  overflow: hidden;
  animation: ${pop} 0.5s ease-out;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  position: relative;
  width: 100%;
  max-width: 2000px;
  margin-left: auto;
  margin-right: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    right: -10px;
    width: 40px;
    height: 40px;
    background: ${props => props.theme.accent};
    border-radius: 50%;
    opacity: 0.4;
  }
  
  &:hover {
    box-shadow: 0 20px 45px rgba(63, 140, 255, 0.25);
    transform: translateY(-4px);
  }
  
  @media (max-width: 768px) {
    border-radius: 15px;
    width: 98%;
  }
`;

const TokenHeader = styled.div`
  background: linear-gradient(135deg, 
    ${props => props.theme.backgroundAlt}, 
    ${props => props.theme === darkTheme ? 
      'rgba(19, 47, 76, 0.15)' : 
      'rgba(63, 140, 255, 0.15)'});
  padding: 1.5rem;
  border-bottom: 4px solid ${props => props.theme.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, 
      ${props => props.theme.primary}, 
      ${props => props.theme.secondary}, 
      ${props => props.theme.accent}, 
      ${props => props.theme.primary});
    background-size: 300% 100%;
    animation: ${shimmer} 6s linear infinite;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    right: 10px;
    width: 8px;
    height: 8px;
    background-color: ${props => props.theme.accent};
    border-radius: 50%;
    animation: ${pulse} 2s infinite;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TokenTitle = styled.h3`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  color: ${theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: 'Fredoka', sans-serif;
  transform: rotate(0deg);
  text-shadow: 2px 2px 0 rgba(255, 255, 255, 0.8);
  
  &::before {
    display: none; /* Hide the emoji */
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    width: 100%;
  }
`;

const RefreshIndicator = styled.div`
  font-size: 0.8rem;
  color: ${theme.textSecondary};
  margin-left: 1rem;
  font-weight: 500;
  opacity: 0.7;
  
  span {
    font-weight: 600;
    color: ${theme.primary};
  }
  
  .auto-refresh {
    color: ${theme.accent};
    font-weight: 600;
    animation: ${pulse} 2s infinite;
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
  }
`;

const TokenControls = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const SortSelect = styled.select`
  padding: 0.7rem 1.2rem;
  border-radius: 15px;
  border: 3px solid ${theme.borderLight};
  background: white;
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.textPrimary};
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  font-family: 'Fredoka', sans-serif;
  transform: rotate(0deg);
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 6px 15px rgba(63, 140, 255, 0.2);
  }
  
  &:hover {
    border-color: ${theme.primary};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0.9rem 1.2rem;
    font-size: 1.1rem;
  }
`;

const FilterInput = styled.input`
  border: 2px solid ${theme.borderLight};
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 0.8rem 1rem;
  font-size: 1rem;
  color: ${theme.textPrimary};
  flex: 1;
  min-width: 120px;
  transition: all 0.25s;
  
  &::placeholder {
    color: ${theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px ${theme.primary}30;
  }
`;

const SearchTip = styled.div`
  font-size: 0.85rem;
  color: ${theme.textSecondary};
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  background-color: rgba(63, 140, 255, 0.08);
  width: 100%;
  max-width: 600px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: ${props => props.show ? 'block' : 'none'};
`;

const CurrencyTabs = styled.div`
  display: flex;
  padding: 0.5rem;
  background: ${theme.backgroundLight};
  justify-content: center;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    padding: 0.8rem 0.5rem;
  }
`;

const CurrencyTab = styled.button`
  padding: 0.8rem 2rem;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  color: ${props => props.active ? theme.primary : theme.textSecondary};
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: ${props => props.active ? '0 5px 15px rgba(63, 140, 255, 0.15)' : 'none'};
  transform: ${props => props.active ? 'scale(1.05)' : 'scale(1)'};
  font-family: 'Fredoka', sans-serif;
  
  &:hover {
    background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
    transform: ${props => props.active ? 'scale(1.05)' : 'translateY(-2px)'};
  }
  
  @media (max-width: 480px) {
    flex: 1;
    padding: 1rem 1rem;
    font-size: 1.2rem;
  }
`;

const TokenList = styled.div`
  max-height: 600px;
  overflow-y: auto;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  grid-gap: 1.5rem;
  
  &::-webkit-scrollbar {
    width: 10px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(63, 140, 255, 0.4);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(63, 140, 255, 0.6);
  }
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const TokenCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.06);
  border: 3px solid ${theme.borderLight};
  transition: all 0.25s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  animation: ${pop} 0.5s forwards;
  animation-delay: ${props => props.index * 0.1}s;
  opacity: 0;
  transform: translateY(0);
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(63, 140, 255, 0.2);
    border-color: ${theme.primary}80;
  }
  
  &:active {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(63, 140, 255, 0.15);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.8), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  
  &:hover::after {
    transform: translateX(100%);
  }
`;

const TokenInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
`;

const TokenNameSection = styled.div`
  display: flex;
  align-items: center;
`;

const TokenIconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 15px;
  position: relative;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border: 3px solid white;
  animation: ${float} 3s ease-in-out infinite;
  flex-shrink: 0;
  background: white;
`;

const TokenIconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TokenIconFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${props => props.color || 'linear-gradient(45deg, #6a11cb, #2575fc)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 1.2rem;
  text-transform: uppercase;
  font-family: 'Fredoka', sans-serif;
`;

const TokenIcon = ({ token, fallbackColor }) => {
  const [iconLoaded, setIconLoaded] = useState(false);
  const [iconError, setIconError] = useState(false);
  
  // Try to determine the icon URL
  const iconUrl = useMemo(() => {
    // Debug the token data we're working with
    console.log('Token for icon:', {
      currency: token.currency,
      issuer: token.issuer,
      md5: token.md5,
      name: token.name, 
      ext: token.ext
    });
    
    // If the token has an explicit icon property, use it
    if (token.icon) return token.icon;
    
    // Use the URL pattern shown in the inspection
    if (token.md5) {
      return `https://s1.xrpl.to/token/${token.md5}`;
    }
    
    // Fallback for tokens without md5
    if (token.currency && token.issuer) {
      // Calculate md5 hash for the token
      const md5 = CryptoJS.MD5(token.issuer + '_' + token.currency).toString();
      return `https://s1.xrpl.to/token/${md5}`;
    }
    
    return null;
  }, [token]);
  
  const handleImageLoad = () => {
    console.log('Image loaded successfully:', iconUrl);
    setIconLoaded(true);
  };
  
  const handleImageError = () => {
    console.error('Failed to load image:', iconUrl);
    setIconError(true);
  };
  
  const getInitial = (name) => {
    if (!name) return '?';
    
    if (name.includes(' ')) {
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .substring(0, 2);
    }
    
    return name.substring(0, 2);
  };
  
  return (
    <TokenIconWrapper>
      {iconUrl && !iconError ? (
        <TokenIconImage 
          src={iconUrl}
          alt={token.displayName || 'token icon'}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      ) : (
        <TokenIconFallback color={fallbackColor}>
          {getInitial(token.displayName || token.currency || "??")}
        </TokenIconFallback>
      )}
    </TokenIconWrapper>
  );
};

const TokenName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const TokenSymbol = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${theme.textPrimary};
  margin-bottom: 0.3rem;
  font-family: 'Fredoka', sans-serif;
`;

const TokenIssuer = styled.span`
  font-size: 0.9rem;
  color: ${theme.textSecondary};
  font-family: 'JetBrains Mono', monospace;
`;

const TokenMeta = styled.div`
  font-size: 0.8rem;
  color: ${theme.textSecondary};
  margin-top: 0.2rem;
`;

const PercentChange = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.positive ? theme.success : theme.error};
  background: ${props => props.positive ? 'rgba(0, 200, 83, 0.1)' : 'rgba(255, 82, 82, 0.1)'};
  padding: 0.5rem 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  position: relative;
  animation: ${pulse} 2s infinite;
  font-family: 'Fredoka', sans-serif;
  
  &::before {
    content: ${props => props.positive ? "'▲'" : "'▼'"};
    margin-right: 5px;
    font-size: 0.8rem;
  }
`;

const TokenStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  margin-bottom: 1.2rem;
  flex-grow: 1;
  
  @media (min-width: 1500px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 400px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.8rem 0.5rem;
  background: rgba(63, 140, 255, 0.05);
  border-radius: 15px;
  transition: all 0.2s;
  animation: ${bounce} 2s infinite;
  animation-delay: ${props => props.index * 0.2}s;
  min-width: 0;
  overflow: hidden;
  
  &:hover {
    background: rgba(63, 140, 255, 0.1);
    transform: translateY(-3px);
  }
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: ${theme.textSecondary};
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-align: center;
`;

const StatValue = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${theme.textPrimary};
  font-family: 'Fredoka', sans-serif;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding: 0 4px;
  
  /* Highlight market cap with distinctive styling */
  .market-cap-value {
    font-weight: 800;
    color: ${theme.accent};
  }
`;

const MoreInfoButton = styled.button`
  background: linear-gradient(90deg, ${theme.primary}, ${theme.secondary});
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 15px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  box-shadow: 0 5px 15px rgba(63, 140, 255, 0.3);
  font-family: 'Fredoka', sans-serif;
  position: relative;
  overflow: hidden;
  transform: rotate(0deg);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(63, 140, 255, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(63, 140, 255, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  
  &:hover::after {
    transform: translateX(100%);
  }
`;

const LoadMoreButton = styled.button`
  background: white;
  border: 3px dashed ${theme.primary};
  color: ${theme.primary};
  padding: 1rem 2rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  margin: 1.5rem auto;
  display: block;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  font-family: 'Fredoka', sans-serif;
  
  &:hover {
    background: ${theme.primary}10;
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 8px 20px rgba(63, 140, 255, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  background: rgba(255, 82, 82, 0.05);
  color: ${theme.error};
  border-radius: 15px;
  text-align: center;
  font-weight: 600;
  margin: 1rem 0;
  font-family: 'Quicksand', sans-serif;
  border: 2px dashed ${theme.error}80;
`;

const LoadingSpinner = styled.div`
  width: ${props => props.size || '48px'};
  height: ${props => props.size || '48px'};
  border: 4px solid rgba(0, 112, 243, 0.2);
  border-top: 4px solid #0070f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 30px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const NewTokenToggle = styled.button`
  padding: 0.7rem 1.2rem;
  border-radius: 15px;
  border: 3px solid ${props => props.active ? theme.accent : theme.borderLight};
  background: ${props => props.active ? `linear-gradient(135deg, ${theme.accent}30, ${theme.accent}10)` : 'white'};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? theme.accent : theme.textSecondary};
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: ${props => props.active ? '0 4px 12px rgba(255, 120, 60, 0.2)' : '0 4px 10px rgba(0, 0, 0, 0.05)'};
  transform: rotate(0deg);
  font-family: 'Fredoka', sans-serif;
  
  &:hover {
    border-color: ${theme.accent};
    color: ${theme.accent};
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(255, 120, 60, 0.15);
  }
  
  &::before {
    content: '🔥';
    margin-right: 0.5rem;
    display: ${props => props.active ? 'inline-block' : 'none'};
    animation: ${wiggle} 2s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0.9rem 1.2rem;
    font-size: 1.1rem;
    justify-content: center;
    display: flex;
    align-items: center;
  }
`;

// Add New Badge component
const NewBadge = styled.span`
  background: ${theme.accent};
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 0.5rem;
  animation: ${pulse} 2s infinite;
`;

// Update RefreshButton to be more prominent
const RefreshButton = styled.button`
  background: ${theme.accent}10;
  border: 2px solid ${theme.accent}30;
  color: ${theme.accent};
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  transition: all 0.2s;
  font-weight: bold;
  margin-left: 0.5rem;
  
  &:hover {
    background: ${theme.accent}30;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(255, 120, 60, 0.2);
  }
  
  svg {
    transition: transform 0.5s;
  }
  
  &:hover svg {
    transform: rotate(180deg);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    margin-top: 0.5rem;
  }
`;

// Add a JustIssued badge for tokens created within the last 60 minutes
const JustIssuedBadge = styled.span`
  background: ${theme.error};
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 0.5rem;
  animation: ${pulse} 1s infinite;
`;

// Add a VerifiedBadge for Xpad tokens
const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(0, 200, 83, 0.1);
  color: ${theme.success};
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: bold;
  margin-left: 0.5rem;
  
  svg {
    margin-right: 3px;
  }
`;

// Helper functions
const hexToText = (hex) => {
  if (!hex || typeof hex !== 'string') {
    return hex;
  }
  
  // Special case: if the currency is already a short name like "XRP", return it
  if (hex.length <= 5) {
    return hex;
  }

  // Check if it's a standard hex format with 0x prefix
  if (hex.startsWith('0x')) {
    try {
      const hexWithoutPrefix = hex.slice(2);
      let str = '';
      for (let i = 0; i < hexWithoutPrefix.length; i += 2) {
        const charCode = parseInt(hexWithoutPrefix.substr(i, 2), 16);
        if (charCode > 0) {
          str += String.fromCharCode(charCode);
        }
      }
      return str || hex;
    } catch (e) {
      console.error('Error converting 0x hex to text:', e);
    }
  }
  
  // Standard XRP hex format (no 0x prefix, usually 40 characters of hex)
  if (/^[0-9A-F]+$/i.test(hex) && hex.length === 40) {
    try {
      let str = '';
      for (let i = 0; i < hex.length; i += 2) {
        const charCode = parseInt(hex.substr(i, 2), 16);
        if (charCode > 0 && charCode < 128) { // Only accept ASCII range
          str += String.fromCharCode(charCode);
        }
      }
      
      // If we successfully converted at least some characters, return the result
      // Otherwise, fall back to the original hex
      return str.trim() || hex;
    } catch (e) {
      console.error('Error converting regular hex to text:', e);
    }
  }
  
  // If all conversion attempts fail, return the original value
  return hex;
};

// Shorten address for display purposes
const shortenAddress = (address) => {
  if (!address || typeof address !== 'string') return 'Unknown';
  
  if (address.length <= 12) return address;
  
  // Return format like "rHb9...jipM" 
  return `${address.substring(0, 5)}...${address.substring(address.length - 4)}`;
};

// Format large numbers with commas and better precision
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  
  try {
    let value = parseScientificNotation(num);
    
    if (isNaN(value)) return '0';
    
    // Handle scientific notation 
    if (value.toString().includes('e')) {
      value = parseFloat(value);
    }
    
    // Use abbreviations for large numbers to save space
  if (value >= 1000000000) {
    return (value / 1000000000).toFixed(2) + 'B';
  } else if (value >= 1000000) {
    return (value / 1000000).toFixed(2) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(2) + 'K';
    } else if (value >= 1) {
      return value.toFixed(2);
    } else if (value > 0) {
      // For very small numbers, show scientific notation to save space
  if (value < 0.0001) {
    return value.toExponential(2);
      }
      // Otherwise show decimal places
      return value.toFixed(Math.min(6, value.toString().split('.')[1]?.length || 0));
    }
    
    return value.toString();
  } catch (err) {
    console.error('Error formatting number:', err, 'for value:', num);
    return '0';
  }
};

// Format price with appropriate decimal places for better readability
const formatPrice = (num) => {
  if (num === null || num === undefined) return '0';
  
  try {
    // Convert to number in case it's a string
    let value = parseScientificNotation(num);
    
    // Handle non-numeric values
    if (isNaN(value)) return '0';
    
    // Handle very small numbers - use scientific notation for very small values
    if (value > 0 && value < 0.00001) {
      return value.toExponential(6);
    }
    
    // Format based on the size of the number
    if (value >= 1000000) {
      return (value / 1000000).toFixed(2) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(2) + 'K';
    } else if (value >= 1) {
      return value.toFixed(2);
    } else if (value >= 0.1) {
      return value.toFixed(3);
    } else if (value >= 0.01) {
      return value.toFixed(4);
    } else if (value >= 0.001) {
      return value.toFixed(5);
    } else if (value > 0) {
      return value.toFixed(6);
    }
    
    return value.toString();
  } catch (err) {
    console.error('Error formatting price:', err, 'for value:', num);
    return '0';
  }
};

// Generate a consistent color based on a string
const stringToColor = (str) => {
  if (!str) return '#6a11cb';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    '#3498db', '#2ecc71', '#9b59b6', '#e74c3c', '#f1c40f',
    '#1abc9c', '#e67e22', '#34495e', '#6a11cb', '#2575fc',
    '#4158D0', '#C850C0', '#8E2DE2', '#FF6B6B', '#4A00E0'
  ];
  
  return colors[Math.abs(hash) % colors.length];
};

const getInitial = (name) => {
  if (!name) return '?';
  
  // For multi-word names, get first letter of each word
  if (name.includes(' ')) {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2);
  }
  
  // For single words, get first letter, or first two for short names
  return name.length <= 3 ? name : name.substring(0, 1);
};

// Handle scientific notation in string values (like "1.2e+8")
const parseScientificNotation = (value) => {
  if (!value) return 0;
  
  // If it's already a number, return it
  if (typeof value === 'number') return value;
  
  const strValue = String(value);
  
  // Check if it's in scientific notation
  if (strValue.includes('e') || strValue.includes('E')) {
    try {
      // Parse scientific notation properly
      return parseFloat(strValue);
    } catch (e) {
      console.error('Error parsing scientific notation:', e);
      return 0;
    }
  }
  
  // Regular parsing for non-scientific notation
  try {
    return parseFloat(strValue);
  } catch (e) {
    return 0;
  }
};

// Enhance formatDate to also show time period (matching xrpl.to format)
const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    
    // For tokens created very recently
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays)}d ago`;
    } else if (diffMonths < 12) {
      return `${Math.floor(diffMonths)}mo ago`;
    }
    
    // For older tokens, show date in YYYY-MM-DD format
    return date.toISOString().split('T')[0];
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Unknown';
  }
};

// Function to get minutes ago for very recently issued tokens
const getMinutesAgo = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    const createdDate = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    return diffMinutes <= 60 ? `${diffMinutes}m ago` : '';
  } catch (e) {
    return '';
  }
};

// Function to check if a token is new (created within the last 7 days)
const isNewToken = (timestamp) => {
  if (!timestamp) return false;
  
  try {
    const createdDate = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 14; // Increase to 14 days to catch more new tokens
  } catch (e) {
    return false;
  }
};

// Function to check if a token was just issued (created within the last hour)
const isJustIssued = (timestamp) => {
  if (!timestamp) return false;
  
  try {
    const createdDate = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    return diffMinutes <= 60; // Within the last hour
  } catch (e) {
    return false;
  }
};

// Enrich tokens with metadata and information from tokenGraph
const enrichTokensWithMetadata = async (tokens) => {
  if (!Array.isArray(tokens) || tokens.length === 0) return tokens;
  
  // Process each token to try to find its metadata
  const enrichedTokens = await Promise.all(tokens.map(async (token) => {
    try {
      // If the token already has icon data, don't fetch again
      if (token.icon) return token;
      
      // More robust way to check if a token is issued by Xpad
      const isXpadToken = 
        // Check if token has the xpad flag set (which is set during token creation)
        token.xpad === true ||
        // Check domain metadata for Xpad domains
        (token.domain && (token.domain.includes('xpad.') || token.domain === 'xpad.space')) ||
        // Check if token has created_via metadata set to "xpad"
        token.created_via === "xpad";
      
      // Try to fetch token metadata if we have an issuer and currency
      if (token.issuer && token.currency) {
        // Try to get token info which might include TOML file location
        const tokenInfo = await xrplToService.getTokenInfo(token.issuer, token.currency);
        
        if (tokenInfo && tokenInfo.icon) {
          return { 
            ...token, 
            icon: tokenInfo.icon,
            verified: isXpadToken // Mark as verified if it's an Xpad token
          };
        }
      }
      
      // If we couldn't enrich the token, still set the verified flag if it's an Xpad token
      return {
        ...token,
        verified: isXpadToken
      };
    } catch (err) {
      console.error(`Error enriching token ${token.currency}:`, err);
      return token;
    }
  }));
  
  return enrichedTokens;
};

const AnalyzerButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${theme.primary};
  color: white;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${theme.primaryDark};
    transform: translateY(-2px);
  }
  
  svg {
    stroke-width: 2.5px;
  }
`;

const AnalyzerModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  padding: 20px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid ${theme.borderLight};
  padding-bottom: 15px;
  
  h3 {
    margin: 0;
    color: ${theme.primary};
    font-size: 1.5rem;
  }
  
  button {
    background: none;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: ${theme.textSecondary};
    
    &:hover {
      color: ${theme.error};
    }
  }
`;

const WalletAnalyzerForm = styled.form`
  margin-bottom: 20px;
`;

const WalletInputGroup = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const WalletAddressInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  border: 2px solid ${theme.borderLight};
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    border-color: ${theme.primary};
    outline: none;
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const AnalyzeButton = styled.button`
  padding: 12px 25px;
  background-color: ${theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    background-color: ${theme.primaryDark};
  }
  
  &:disabled {
    background-color: #c5c5c5;
    cursor: not-allowed;
  }
`;

const WalletErrorMessage = styled.div`
  margin: 15px 0;
  padding: 12px 15px;
  background-color: #fff3f3;
  border-left: 4px solid ${theme.error};
  color: #e91e63;
  border-radius: 0 4px 4px 0;
  font-size: 14px;
  line-height: 1.5;
  
  pre {
    margin: 10px 0 0;
    white-space: pre-wrap;
    font-family: inherit;
    font-size: 13px;
    color: #222;
    background: rgba(0,0,0,0.05);
    padding: 8px;
    border-radius: 4px;
  }
`;

const DetailedErrorInfo = styled.div`
  margin-top: 10px;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  
  ul {
    margin: 10px 0 0;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 5px;
  }
  
  .suggestion {
    margin-top: 12px;
    font-weight: 500;
  }
`;

const AnalysisLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px 0;
  text-align: center;
  
  p {
    margin-top: 15px;
    color: ${theme.textSecondary};
  }
  
  small {
    color: ${theme.textTertiary};
    margin-top: 5px;
  }
`;

const AnalysisLoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(58, 134, 255, 0.1);
  border-left-color: #3a86ff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 1rem;
`;

const ReportContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const ReportIframe = styled.iframe`
  width: 100%;
  height: 800px;
  border: none;
`;

const ResultSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${theme.borderLight};
`;

const AnalysisStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 20px 0;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${theme.borderLight};
    border-top: 4px solid ${theme.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }
  
  p {
    color: ${theme.textSecondary};
    font-weight: 600;
  }
`;

const ViewReportButton = styled.a`
  display: inline-block;
  background: ${theme.accent};
  color: white;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  margin-top: 15px;
  text-align: center;
  
  &:hover {
    background: ${theme.secondary};
  }
`;

// Known exchange/service wallets database - for the wallet analyzer
const KNOWN_WALLETS = {
  'rLHzPsX6oXkzU2qL12kHCH8G8cnZv1rBJh': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
  'rU6K7V3Po4snVhBBaU29sesqs2qTQJWDw1': { name: 'Binance Cold Wallet', type: 'exchange', color: '#F0B90B' },
  'rEb8TK3gBgk5auZkwc6sHnwrGVJH8DuaLh': { name: 'Binance Hot Wallet', type: 'exchange', color: '#F0B90B' },
  'rJb5KsHsDHF1YS5B5DU6QCkH5NsPaKQTcy': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
  'rBWpYJhuJWBPAkzJ4kYQqHShSkkF3rgeD': { name: 'Bitrue', type: 'exchange', color: '#2ED3D2' },
  'rNGkUxPVuXQiUV1Qmam2NoXykdvk7KKuGa': { name: 'Uphold', type: 'exchange', color: '#7734BE' },
  'rMQ98K56yXJbDGv49ZSmW51sLn94Xe1mu1': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
  'rw2ciyaNshpHe7bCHo4bRWq6pqqynnWKQg': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
  'rPVMhWBsfF9iMXYj3aAzJVkPDTFNSyWdKy': { name: 'Bittrex', type: 'exchange', color: '#1C2226' },
  'rG6FZ31hDHN1K5Dkbma3PSB5uVCuVVRzfn': { name: 'Bitso', type: 'exchange', color: '#FF0151' },
  'rLkMJhSVwhmummLjJPVrfPZVdrjYZokQjy': { name: 'Bitstamp', type: 'exchange', color: '#1B97E9' },
  'rJQi8gFZafF3eiXRJ9o9mxsrz8u9MfcJkk': { name: 'BitGo', type: 'exchange', color: '#022C4F' },
  'rUzWJkXyqnF8IAhSQbdBtDLLnrXu5j4Hln': { name: 'Bithumb', type: 'exchange', color: '#FB4319' },
  'rK59hSXoeaJxzWb8vQKyPpgrX69GpGFCzn': { name: 'Kraken', type: 'exchange', color: '#5741D9' },
  'rNxp4h8apvRis6mJf9S1hithQWFHrCXgVg': { name: 'CoinOne', type: 'exchange', color: '#3FD0AE' },
  'r9HwsqBnAUN4nF6nDqxd4sgP8DrDnDcZP3': { name: 'XRPL Labs', type: 'service', color: '#1E90FF' },
};

// Custom utility function to convert drops to XRP without relying on xrpl library
const dropsToXrp = (drops) => {
  if (!drops) return 0;
  
  try {
    // Parse the drops string to a number
    let dropsNum;
    
    if (typeof drops === 'string') {
      // Remove any commas or non-numeric characters except decimal points and minus signs
      const cleanedDrops = drops.replace(/[^\d.-]/g, '');
      dropsNum = parseInt(cleanedDrops, 10);
    } else {
      dropsNum = Number(drops);
    }
    
    if (isNaN(dropsNum)) {
      console.warn('Invalid drops value:', drops);
      return 0;
    }
    
    // Convert drops to XRP (1 XRP = 1,000,000 drops)
    return dropsNum / 1000000;
  } catch (error) {
    console.error('Error converting drops to XRP:', error, 'for value:', drops);
    return 0;
  }
};

// List of public XRPL nodes to try
const XRPL_SERVERS = [
  'wss://s1.ripple.com',
  'wss://s2.ripple.com',
  'wss://xrplcluster.com',
  'wss://xrpl.ws',
  'wss://s.altnet.rippletest.net:51233',
  'wss://rippled.xrptipbot.com'
];

// Alternative HTTP endpoints for when WebSockets fail
const XRPL_HTTP_ENDPOINTS = [
  'https://xrplcluster.com',
  'https://s1.ripple.com:51234',
  'https://s2.ripple.com:51234',
  'https://api.xrpscan.com/api/v1/node',
  'https://data.ripple.com/v2',
  'https://rippled.xrptipbot.com:51234'
];

// Utility functions for the wallet analyzer
const rippleTimeToUnixTime = (rpepoch) => {
  return (rpepoch + 946684800) * 1000;
};

const rippleTimeToDate = (rpepoch) => {
  return new Date(rippleTimeToUnixTime(rpepoch));
};

const hexToString = (hex) => {
  try {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('')) return '';
    
    // Remove '0x' prefix if present
    const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
    
    // Convert hex to buffer then to string
    const str = cleanHex
      .match(/.{1,2}/g)
      ?.map(byte => String.fromCharCode(parseInt(byte, 16)))
      .join('');
      
    // Check if the result is printable ASCII
    if (str && /^[\x20-\x7E]*$/.test(str)) {
      return str;
    }
    return ''; // Not a valid string
  } catch (err) {
    console.error('Error converting hex to string:', err);
    return '';
  }
};

// Add this utility function near the other utility functions (around line 1490)
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Enhanced backoff mechanism
const rateLimit = {
  failures: new Map(),  // Track failures by endpoint
  maxBackoffTime: 30000, // Max 30 seconds
  baseDelay: 2000,     // Start with 2 seconds
  
  // Record a failure for an endpoint
  recordFailure(endpoint) {
    const currentCount = this.failures.get(endpoint) || 0;
    this.failures.set(endpoint, currentCount + 1);
    return currentCount + 1;
  },
  
  // Reset failures for an endpoint after successful request
  resetFailures(endpoint) {
    this.failures.set(endpoint, 0);
  },
  
  // Get backoff time with jitter for an endpoint
  getBackoffTime(endpoint) {
    const failureCount = this.failures.get(endpoint) || 0;
    // Exponential backoff with jitter to prevent thundering herd
    const baseTime = Math.min(
      this.baseDelay * Math.pow(2, failureCount),
      this.maxBackoffTime
    );
    // Add jitter (±20%)
    const jitter = baseTime * 0.2 * (Math.random() - 0.5);
    return Math.floor(baseTime + jitter);
  },
  
  // Sleep with backoff for a specific endpoint
  async backoff(endpoint) {
    const failures = this.recordFailure(endpoint);
    const delay = this.getBackoffTime(endpoint);
    console.log(`Rate limit backoff: ${delay}ms for ${endpoint} (failures: ${failures})`);
    await sleep(delay);
  }
};

// This function can be removed or kept as a fallback
const getBackoffTime = (failures) => {
  // Start with 1 second, double each time, max 10 seconds
  return Math.min(1000 * Math.pow(2, failures), 10000);
};

// Add the wallet analyzer functionality to the TokenFeed component
const TokenFeed = ({ onTokenSelect, openWindowLink }) => {
  const { theme: currentTheme } = useContext(ThemeContext);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('trendingScore');
  const [sortType, setSortType] = useState('desc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [showNewTokens, setShowNewTokens] = useState(false);
  const [currency, setCurrency] = useState('usd');
  const [xrpPrice, setXrpPrice] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  
  // Wallet analyzer state variables
  const [showAnalyzerModal, setShowAnalyzerModal] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, status: '' });
  
  // Search related state
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [specificTokenData, setSpecificTokenData] = useState(null);
  
  const limit = 15;
  
  // At the top of the file, change the AUTO_REFRESH_INTERVAL to 15 seconds (line ~1298)
  const AUTO_REFRESH_INTERVAL = 15000; // 15 seconds for real-time updates

  // Fetch and set the current XRP price
  const fetchXrpPrice = async () => {
    console.log('Fetching XRP price...');
    
    try {
      // Only allow reasonable XRP prices between $0.10 and $5.00
      const validatePrice = (price) => {
        const numPrice = parseFloat(price);
        return !isNaN(numPrice) && numPrice >= 0.10 && numPrice <= 5.00;
      };
      
      // Helper function to update the price consistently
      const updatePrice = (source, price) => {
        if (!validatePrice(price)) {
          console.warn(`Invalid price received from ${source}: $${price}`);
          return false;
        }
        
        console.log(`${source} XRP price: $${price}`);
        setXrpPrice(price);
        localStorage.setItem('xrp_price', price);
        localStorage.setItem('xrp_price_timestamp', Date.now());
        return true;
      };

      // Try CoinGecko API first (most accurate)
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ripple&vs_currencies=usd', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors',
          timeout: 3000 // 3 second timeout
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.ripple && data.ripple.usd) {
            if (updatePrice('CoinGecko', data.ripple.usd)) return;
          }
        }
      } catch (err) {
        console.warn('CoinGecko API failed:', err.message);
      }
      
      // Try Binance API as second option
      try {
        const binanceResponse = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=XRPUSDT', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors',
          timeout: 3000 // 3 second timeout
        });
        
        if (binanceResponse.ok) {
          const binanceData = await binanceResponse.json();
          if (binanceData && binanceData.price) {
            if (updatePrice('Binance', binanceData.price)) return;
          }
        }
      } catch (err) {
        console.warn('Binance API failed:', err.message);
      }
      
      // Try CoinCap API as third option
      try {
        const coinCapResponse = await fetch('https://api.coincap.io/v2/assets/xrp', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors',
          timeout: 3000 // 3 second timeout
        });
        
        if (coinCapResponse.ok) {
          const coinCapData = await coinCapResponse.json();
          if (coinCapData && coinCapData.data && coinCapData.data.priceUsd) {
            if (updatePrice('CoinCap', coinCapData.data.priceUsd)) return;
          }
        }
      } catch (err) {
        console.warn('CoinCap API failed:', err.message);
      }

      // Check for cached price as fallback
      const cachedPrice = localStorage.getItem('xrp_price');
      const timestamp = localStorage.getItem('xrp_price_timestamp');
      
      if (cachedPrice && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        const sixHoursInMs = 6 * 60 * 60 * 1000;
        
        if (cacheAge < sixHoursInMs && validatePrice(cachedPrice)) {
          console.log(`Using cached XRP price from ${Math.round(cacheAge/1000/60)} minutes ago: $${cachedPrice}`);
          setXrpPrice(parseFloat(cachedPrice));
          return;
        }
      }
      
      // If no price could be obtained, use a default value
      if (!xrpPrice) {
        const defaultPrice = 0.51;
        console.log(`Using default XRP price: $${defaultPrice}`);
        setXrpPrice(defaultPrice);
      }
    } catch (error) {
      console.error('Error in fetchXrpPrice:', error);
      // Set a default price if we have no price yet
      if (!xrpPrice) {
        setXrpPrice(0.51);
      }
    }
  };

  // Get the current XRP price - with reliable fallback
  const getXrpPrice = () => {
    // If we have a fetched price and it's reasonable, use it
    if (xrpPrice && xrpPrice > 0 && xrpPrice < 10) return xrpPrice;
    
    // Check for cached price in localStorage as a backup
    try {
      const cachedPrice = localStorage.getItem('xrp_price');
      const timestamp = localStorage.getItem('xrp_price_timestamp');
      
      if (cachedPrice && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
        
        // Use cache if it's less than 24 hours old (more generous than before)
        if (cacheAge < twentyFourHoursInMs) {
          const price = parseFloat(cachedPrice);
          if (price > 0 && price < 10) { // Sanity check on price
            return price;
          }
        }
      }
    } catch (err) {
      console.error('Error reading cached XRP price:', err);
    }
    
    // If no valid price is available, trigger a fetch but don't return null
    // This prevents UI glitches with "loading" states
    fetchXrpPrice();
    
    // Return a reasonable fallback price
    return 0.51; // Default conservative fallback
  };

  // Fetch current XRP price when component mounts and periodically refresh
  useEffect(() => {
    // Initial fetch
    fetchXrpPrice();
    
    // Refresh price every 30 seconds for more reliability
    const intervalId = setInterval(fetchXrpPrice, 30 * 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  const fetchTokens = async (reset = false) => {
    try {
      setLoading(true);
      
      const newPage = reset ? 0 : page;
      
      // Set the initial loading higher limit for new tokens
      const initialLimit = showNewTokens ? 50 : limit;
      
      let response;
      
      // Check if the filter looks like an issuer address (r...)
      const isIssuerSearch = filterText.startsWith('r') && filterText.length >= 25;
      const possibleCurrencyMatch = filterText.match(/([a-zA-Z0-9]{3,4})$/);
      
      // Try to detect if user is searching for a specific token by issuer and currency
      if (isIssuerSearch) {
        console.log('Detected possible issuer address search:', filterText);
        
        // Check if there's a possible currency code at the end
        let issuer = filterText;
        let currency = '';
        
        if (possibleCurrencyMatch) {
          // The search might include both issuer and currency
          // Try to extract them using pattern "rXXX...XXX USD"
          const parts = filterText.split(' ');
          if (parts.length >= 2) {
            issuer = parts[0];
            currency = parts[parts.length - 1];
          }
        }
        
        // If we have a clear issuer, try the direct lookup
        if (issuer.startsWith('r') && issuer.length >= 25) {
          try {
            if (currency && currency.length >= 3) {
              // We have both issuer and currency, do specific lookup
              console.log(`Looking up specific token: ${issuer}_${currency}`);
              response = await xrplToService.getTokenByIssuerAndCurrency(issuer, currency);
            } else {
              // Just have issuer, use standard filtering
              response = await xrplToService.getTokens(
                newPage * limit, 
                initialLimit, 
                sortBy, 
                sortType, 
                issuer, 
                false
              );
            }
          } catch (error) {
            console.error('Issuer-specific search failed, falling back to normal search', error);
            // Fall back to normal search
          }
        }
      }
      
      // If we don't have a response yet, use the standard search
      if (!response) {
        // Custom timeout for better UX
        const fetchTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 15000);
        });
        
        // Race the fetch against a timeout
        try {
          response = await Promise.race([
            xrplToService.getTokens(
              newPage * limit, 
              initialLimit, 
              sortBy, 
              sortType, 
              filterText, 
              showNewTokens
            ),
            fetchTimeout
          ]);
        } catch (timeoutError) {
          console.error('Token fetch timeout:', timeoutError);
          setError('Connection timeout. Please try again.');
          setLoading(false);
          return;
        }
      }
      
      // Update the last refresh time whenever we fetch new data
      setLastRefreshTime(Date.now());
      
      // Debug entire API response
      console.log('Full API response:', response);
      
      // Handle the full API response structure
      let tokenArray = [];
      let ratesInfo = {};
      
      // Check for the expected full response structure
      if (response && response.result === 'success' && Array.isArray(response.tokens)) {
        tokenArray = response.tokens;
        
        // Extract exchange rates if available
        if (response.exch) {
          ratesInfo = response.exch;
          // Only update XRP price from API if we don't have one yet
          if (response.exch.USD && getXrpPrice() === null) {
            const rate = parseFloat(response.exch.USD);
            if (!isNaN(rate) && rate > 0) {
              setXrpPrice(rate);
              console.log('Using XRP price from API response as fallback:', rate);
            }
          }
        }
        
        console.log('API response structure:', {
          result: response.result,
          tokenCount: tokenArray.length,
          rates: ratesInfo
        });
      } 
      // Fallback to previous handling methods
      else if (Array.isArray(response)) {
        tokenArray = response;
      } else if (response && Array.isArray(response.tokens)) {
        tokenArray = response.tokens;
      } else if (response && typeof response === 'object') {
        // Last fallback: handle when response is an object with tokens
        if (response.tokens && Array.isArray(response.tokens)) {
          tokenArray = response.tokens;
        } else {
          // Convert object to array if it's a plain object
          const possibleArray = Object.values(response).filter(item => 
            item && typeof item === 'object' && item.currency && item.issuer
          );
          
          if (possibleArray.length > 0) {
            tokenArray = possibleArray;
          } else {
            // Last fallback: try to extract any recognizable token objects
            tokenArray = Object.values(response);
          }
        }
      }
      
      // Filter out invalid tokens (must have issuer and currency)
      tokenArray = tokenArray.filter(token => 
        token && token.issuer && token.currency
      );
      
      console.log('Processed tokens:', tokenArray.length);
      
      // Process tokens to ensure human-readable names and normalize number values
      const processedTokens = tokenArray.map(token => {
        // Properly convert currency to display name
        let displayName = token.currency;
        
        // Try to convert hex currencies to text
        if (token.currency && typeof token.currency === 'string' && token.currency.length === 40) {
          const converted = hexToText(token.currency);
          if (converted && converted !== token.currency) {
            displayName = converted.trim();
          }
        }
        
        // Calculate MD5 hash for lookup if not provided
        let md5 = token.md5;
        if (!md5 && token.issuer && token.currency) {
          md5 = CryptoJS.MD5(token.issuer + '_' + token.currency).toString();
        }
        
        return {
          ...token,
          displayName: displayName || 'Unknown Token',
          md5: md5,
          // Ensure number values are present, defaulting to 0 if missing
          exch: token.exch !== undefined ? token.exch : "0",
          vol24hxrp: token.vol24hxrp !== undefined ? token.vol24hxrp : "0",
          marketcap: token.marketcap !== undefined ? token.marketcap : "0", 
          pro24h: token.pro24h !== undefined ? token.pro24h : "0",
          trustlines: token.trustlines !== undefined ? token.trustlines : "0"
        };
      });
      
      // Try to enrich tokens with metadata
      const enrichedTokens = await enrichTokensWithMetadata(processedTokens);
      
      // When sorting by creation date, filter only very new tokens
      let finalTokens = enrichedTokens || [];
      
      // If sorting by creation date, ensure the order is correct (newest first)
      if (sortBy === 'createdAt') {
        finalTokens = [...finalTokens].sort((a, b) => {
          // Use dateon field if available, otherwise fall back to createdAt
          const dateFieldA = a.dateon || a.createdAt;
          const dateFieldB = b.dateon || b.createdAt;
          
          // Get timestamps or default to 0
          const dateA = dateFieldA ? new Date(dateFieldA).getTime() : 0;
          const dateB = dateFieldB ? new Date(dateFieldB).getTime() : 0;
          
          // Sort by creation date (newest first)
          return dateB - dateA;
        });
        
        console.log('Sorted tokens by creation date (newest first)');
      }
      
      // Extra filtering for new tokens if needed
      if (sortBy === 'createdAt' && showNewTokens) {
        finalTokens = finalTokens.filter(token => {
          // Try to determine if a token is new (either from createdAt or other fields)
          return token.createdAt && isNewToken(token.createdAt);
        });
        
        console.log(`Filtered down to ${finalTokens.length} new tokens based on creation date`);
      }
      
      // Check if we've reached the end of the available tokens
      if (!finalTokens || finalTokens.length < limit) {
        setHasMore(false);
      }
      
      if (reset) {
        setTokens(finalTokens || []);
        setPage(1);
      } else {
        setTokens([...tokens, ...(finalTokens || [])]);
        setPage(page + 1);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load tokens. Please try again later.');
      console.error('Error fetching tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens(true);
  }, [sortBy, sortType, filterText, showNewTokens]);
  
  // Define constant to hold the mobile detection
  const isMobileDevice = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);
  
  // Auto-refresh tokens periodically for real-time data, with mobile-specific handling
  useEffect(() => {
    console.log(`Setting up real-time auto-refresh interval (Mobile: ${isMobileDevice ? 'Yes' : 'No'})...`);
    
    // Use a different refresh interval for mobile
    const refreshInterval = isMobileDevice ? AUTO_REFRESH_INTERVAL * 4 : AUTO_REFRESH_INTERVAL; // Much longer interval for mobile
    
    // Track if app is active or in background
    let isAppActive = !document.hidden;
    
    // Add visibility change listener to detect when app goes to background
    const handleVisibilityChange = () => {
      isAppActive = !document.hidden;
      console.log(`App visibility changed: ${isAppActive ? 'active' : 'background'}`);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const intervalId = setInterval(() => {
      // Skip auto-refresh if:
      // 1. Search is in progress
      // 2. App is in background (document.hidden)
      // 3. On mobile and not actively interacting with the page
      if (searchLoading || !isAppActive || (isMobileDevice && document.hidden)) {
        console.log('Skipping auto-refresh due to background state or ongoing search');
        return;
      }
      
      console.log('Auto-refreshing token list in real-time...');
      
      // Use a separate function for auto-refresh that won't trigger UI resets
      const refreshTokensOnly = async () => {
        try {
          // Don't set loading to true during auto-refresh to prevent UI flicker
          const response = await xrplToService.getTokens(
            0, 
            limit, 
            sortBy, 
            sortType, 
            filterText, 
            showNewTokens
          );
          
          // Add random cache-busting parameter to avoid mobile browser caching
          // This helps prevent stale data on mobile
          if (isMobileDevice) {
            response._cacheBuster = Date.now();
          }
          
          // Process response similar to fetchTokens but without reset UI state
          if (response && response.result === 'success' && Array.isArray(response.tokens)) {
            // Process tokens as in fetchTokens
            const processedTokens = response.tokens
              .filter(token => token && token.issuer && token.currency)
              .map(token => {
                // Token processing logic from fetchTokens
                let displayName = token.currency;
                if (token.currency && typeof token.currency === 'string' && token.currency.length === 40) {
                  const converted = hexToText(token.currency);
                  if (converted && converted !== token.currency) {
                    displayName = converted.trim();
                  }
                }
                
                let md5 = token.md5;
                if (!md5 && token.issuer && token.currency) {
                  md5 = CryptoJS.MD5(token.issuer + '_' + token.currency).toString();
                }
                
                return {
                  ...token,
                  displayName: displayName || 'Unknown Token',
                  md5: md5,
                  exch: token.exch !== undefined ? token.exch : "0",
                  vol24hxrp: token.vol24hxrp !== undefined ? token.vol24hxrp : "0",
                  marketcap: token.marketcap !== undefined ? token.marketcap : "0", 
                  pro24h: token.pro24h !== undefined ? token.pro24h : "0",
                  trustlines: token.trustlines !== undefined ? token.trustlines : "0"
                };
              });
            
            // For mobile devices, only update if the new data is significantly different
            // to avoid unnecessary re-renders
            if (isMobileDevice) {
              const currentTokensLength = tokens.length;
              const newTokensLength = processedTokens.length;
              
              // Skip update if the arrays are identical in length and we're not showing new tokens
              // This helps prevent unnecessary re-renders on mobile
              if (!showNewTokens && 
                  currentTokensLength === newTokensLength && 
                  currentTokensLength > 0) {
                console.log('Skipping token update as data is similar (mobile optimization)');
                setLastRefreshTime(Date.now()); // Still update the refresh time
                return;
              }
            }
            
            // Update tokens without reset - using functional state update
            // to avoid state inconsistencies
            enrichTokensWithMetadata(processedTokens).then(enrichedTokens => {
              // Only update state if component is still mounted and new data exists
              if (enrichedTokens && enrichedTokens.length > 0) {
                setTokens(enrichedTokens);
                setLastRefreshTime(Date.now());
              }
            });
          }
        } catch (err) {
          console.error('Silent auto-refresh error:', err);
          // Don't show errors for background refreshes
        }
      };
      
      // Call the separate refresh function
      refreshTokensOnly();
    }, refreshInterval);
    
    // Clean up on unmount
    return () => {
      console.log('Cleaning up auto-refresh interval');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [sortBy, sortType, filterText, showNewTokens, limit, searchLoading, isMobileDevice, tokens.length]);

  // Add an effect to refresh tokens immediately when showNewTokens changes
  useEffect(() => {
    if (showNewTokens) {
      // Force clear cache and refresh data immediately when showing new tokens
      xrplToService.clearCache();
      
      // When showing new tokens, always sort by creation date
      if (sortBy !== 'createdAt') {
        setSortBy('createdAt');
        setSortType('desc');
      }
      
      fetchTokens(true);
    }
  }, [showNewTokens]);

  // Listen for token creation events from App.js
  useEffect(() => {
    // Check localStorage for recently created tokens on initial load
    const checkForNewToken = () => {
      try {
        const tokenJSON = localStorage.getItem('lastCreatedToken');
        if (tokenJSON) {
          const tokenInfo = JSON.parse(tokenJSON);
          const createdAt = new Date(tokenInfo.createdAt);
          const now = new Date();
          const fiveMinutes = 5 * 60 * 1000;
          
          // If token was created within last 5 minutes, refresh the feed
          if ((now - createdAt) < fiveMinutes) {
            console.log('Recently created token found, refreshing token feed:', tokenInfo);
            xrplToService.clearCache();
            
            // Switch to "new tokens" view
            if (!showNewTokens) {
              setShowNewTokens(true);
            }
            
            // Refresh tokens list
            fetchTokens(true);
          }
        }
      } catch (error) {
        console.error('Error checking for recently created tokens:', error);
      }
    };
    
    // Handle token creation events from App.js
    const handleTokenCreated = (event) => {
      console.log('Token created event received:', event.detail);
      xrplToService.clearCache();
      
      // Switch to "new tokens" view
      if (!showNewTokens) {
        setShowNewTokens(true);
      }
      
      // Refresh tokens list
      fetchTokens(true);
    };
    
    // Check for recently created tokens on component mount
    checkForNewToken();
    
    // Add event listener for future token creations
    window.addEventListener('tokenCreated', handleTokenCreated);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('tokenCreated', handleTokenCreated);
    };
  }, []);

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    
    // For creation date sorting, apply the right settings
    if (e.target.value === 'createdAt') {
      setSortType('desc'); // Newest first is most intuitive
    }
  };

  const handleSortTypeChange = (e) => {
    setSortType(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
    
    // Clear specific token data when search changes
    if (specificTokenData) {
      setSpecificTokenData(null);
    }
    
    // Clear any previous search errors
    if (searchError) {
      setSearchError(null);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchTokens();
    }
  };

  const handleTokenClick = (token) => {
    console.log('Token clicked:', token);
    
    // Show visual feedback that the token was clicked
    setTimeout(() => {
      if (onTokenSelect && typeof onTokenSelect === 'function') {
        try {
          onTokenSelect(token);
        } catch (err) {
          console.error('Error in onTokenSelect callback:', err);
        }
      } else {
        // Create view functions with mobile-friendly link opening
        const viewOnXrplTo = () => {
          if (token.md5) {
            const url = `https://xrpl.to/token/${token.md5}`;
            if (openWindowLink) {
              openWindowLink(url);
            } else {
              // Use direct navigation for mobile
              if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                window.location.href = url; // Direct navigation prevents redirection issues
              } else {
                window.open(url, '_blank');
              }
            }
          } else if (token.currency && token.issuer) {
            const md5 = CryptoJS.MD5(token.issuer + '_' + token.currency).toString();
            const url = `https://xrpl.to/token/${md5}`;
            if (openWindowLink) {
              openWindowLink(url);
            } else {
              // Use direct navigation for mobile
              if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                window.location.href = url; // Direct navigation prevents redirection issues
              } else {
                window.open(url, '_blank');
              }
            }
          }
        };
        
        const viewOnFirstLedger = () => {
          if (token.currency && token.issuer) {
            const url = `https://firstledger.net/token/${token.issuer}/${token.currency}`;
            if (openWindowLink) {
              openWindowLink(url);
            } else {
              // Use direct navigation for mobile
              if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                window.location.href = url; // Direct navigation prevents redirection issues
              } else {
                window.open(url, '_blank');
              }
            }
          }
        };
        
        const viewOnDexScreener = () => {
          if (token.currency && token.issuer) {
            // Format for DexScreener URL: https://dexscreener.com/xrpl/{currencyInHex}.{issuerAddress}_xrp
            const currencyHex = token.currency.length <= 3 
              ? token.currency.padEnd(40, '0').toUpperCase() 
              : token.currency; // For currency codes longer than 3 chars, assume it's already hex
            
            const url = `https://dexscreener.com/xrpl/${currencyHex}.${token.issuer}_xrp`;
            
            if (openWindowLink) {
              openWindowLink(url);
            } else {
              // Use direct navigation for mobile
              if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                window.location.href = url; // Direct navigation prevents redirection issues
              } else {
                window.open(url, '_blank');
              }
            }
          }
        };
        
        // Create dialog with theme-aware styling
        const dialog = document.createElement('div');
        dialog.style.position = 'fixed';
        dialog.style.top = '50%';
        dialog.style.left = '50%';
        dialog.style.transform = 'translate(-50%, -50%)';
        dialog.style.background = currentTheme === darkTheme ? '#132F4C' : 'white';
        dialog.style.padding = '20px';
        dialog.style.borderRadius = '12px';
        dialog.style.boxShadow = currentTheme === darkTheme ? 
          '0 4px 20px rgba(0,0,0,0.5)' : 
          '0 4px 20px rgba(0,0,0,0.2)';
        dialog.style.zIndex = '10000';
        dialog.style.minWidth = '300px';
        dialog.style.maxWidth = '500px';
        dialog.style.width = '90%';
        dialog.style.textAlign = 'center';
        dialog.style.color = currentTheme === darkTheme ? '#E3F2FD' : '#1a1a1a';
        
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.background = 'rgba(0,0,0,0.5)';
        overlay.style.zIndex = '9999';
        
        const title = document.createElement('h3');
        title.textContent = `View ${token.displayName || 'Token'} on:`;
        title.style.margin = '0 0 20px 0';
        title.style.fontSize = '20px';
        title.style.fontWeight = 'bold';
        title.style.color = currentTheme === darkTheme ? '#E3F2FD' : '#1a1a1a';
        
        // Create button container for better layout
        const buttonContainer = document.createElement('div');
        buttonContainer.style.display = 'flex';
        buttonContainer.style.flexDirection = 'column';
        buttonContainer.style.gap = '10px';

        // XRPL.to button
        const xrplButton = document.createElement('button');
        xrplButton.textContent = 'View on XRPL.to';
        xrplButton.style.padding = '12px 15px';
        xrplButton.style.border = 'none';
        xrplButton.style.borderRadius = '8px';
        xrplButton.style.background = '#3F8CFF';
        xrplButton.style.color = 'white';
        xrplButton.style.cursor = 'pointer';
        xrplButton.style.fontWeight = 'bold';
        xrplButton.style.fontSize = '14px';
        xrplButton.style.width = '100%';
        xrplButton.onclick = () => {
          document.body.removeChild(dialog);
          document.body.removeChild(overlay);
          // Add a small delay before navigation to ensure dialog is removed
          setTimeout(() => {
            viewOnXrplTo();
          }, 50);
        };
        
        // FirstLedger button
        const firstLedgerButton = document.createElement('button');
        firstLedgerButton.textContent = 'View on FirstLedger';
        firstLedgerButton.style.padding = '12px 15px';
        firstLedgerButton.style.border = 'none';
        firstLedgerButton.style.borderRadius = '8px';
        firstLedgerButton.style.background = '#EAB02F';
        firstLedgerButton.style.color = 'white';
        firstLedgerButton.style.cursor = 'pointer';
        firstLedgerButton.style.fontWeight = 'bold';
        firstLedgerButton.style.fontSize = '14px';
        firstLedgerButton.style.width = '100%';
        firstLedgerButton.onclick = () => {
          document.body.removeChild(dialog);
          document.body.removeChild(overlay);
          // Add a small delay before navigation to ensure dialog is removed
          setTimeout(() => {
            viewOnFirstLedger();
          }, 50);
        };
        
        // DexScreener button
        const dexScreenerButton = document.createElement('button');
        dexScreenerButton.textContent = 'View on DexScreener';
        dexScreenerButton.style.padding = '12px 15px';
        dexScreenerButton.style.border = 'none';
        dexScreenerButton.style.borderRadius = '8px';
        dexScreenerButton.style.background = '#FF4A68';
        dexScreenerButton.style.color = 'white';
        dexScreenerButton.style.cursor = 'pointer';
        dexScreenerButton.style.fontWeight = 'bold';
        dexScreenerButton.style.fontSize = '14px';
        dexScreenerButton.style.width = '100%';
        dexScreenerButton.onclick = () => {
          document.body.removeChild(dialog);
          document.body.removeChild(overlay);
          // Add a small delay before navigation to ensure dialog is removed
          setTimeout(() => {
            viewOnDexScreener();
          }, 50);
        };
        
        // Separator
        const separator = document.createElement('hr');
        separator.style.margin = '15px 0';
        separator.style.border = '0';
        separator.style.borderTop = '1px solid #eee';
        
        // Cancel button
        const closeButton = document.createElement('button');
        closeButton.textContent = 'Cancel';
        closeButton.style.padding = '12px 15px';
        closeButton.style.border = 'none';
        closeButton.style.borderRadius = '8px';
        closeButton.style.background = '#f0f0f0';
        closeButton.style.color = '#333';
        closeButton.style.cursor = 'pointer';
        closeButton.style.fontWeight = 'bold';
        closeButton.style.fontSize = '14px';
        closeButton.style.width = '100%';
        closeButton.onclick = () => {
          document.body.removeChild(dialog);
          document.body.removeChild(overlay);
        };
        
        // Add buttons to container
        buttonContainer.appendChild(xrplButton);
        buttonContainer.appendChild(firstLedgerButton);
        buttonContainer.appendChild(dexScreenerButton);
        buttonContainer.appendChild(separator);
        buttonContainer.appendChild(closeButton);
        
        // Add elements to dialog
        dialog.appendChild(title);
        dialog.appendChild(buttonContainer);
        
        document.body.appendChild(overlay);
        document.body.appendChild(dialog);
        
        overlay.onclick = () => {
          document.body.removeChild(dialog);
          document.body.removeChild(overlay);
        };
      }
    }, 100);
  };
  
  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
  };

  // Calculate market cap directly from token supply and exchange rate
  const getMarketCap = (token) => {
    if (!token) return 0;
    
    try {
      // Directly use marketcap value from API if available (most reliable)
      if (token.marketcap && !isNaN(parseFloat(token.marketcap))) {
        const marketCap = parseFloat(token.marketcap);
        return marketCap;
      }
      
      // Otherwise calculate from supply * price if both are available
      if (token.supply && token.exch) {
        const supply = parseFloat(token.supply);
        const price = parseFloat(token.exch);
        
        if (!isNaN(supply) && !isNaN(price)) {
          const calculatedMarketCap = supply * price;
          return calculatedMarketCap;
        }
      }
      
      // Return 0 if we can't calculate
      return 0;
    } catch (e) {
      console.error('Market cap calculation error:', e);
      return 0;
    }
  };

  // Calculate USD value based on XRP amount with improved precision
  const calculateUsd = (xrpAmount) => {
    if (xrpAmount === null || xrpAmount === undefined) return 0;
    
    let amount;
    try {
      // Handle scientific notation or string values
      amount = parseScientificNotation(xrpAmount);
      if (isNaN(amount)) amount = 0;
      
      // Get the current XRP price
      const price = getXrpPrice();
      
      // Use a reasonable fallback price if we don't have an up-to-date price
      // This prevents constantly showing "loading" states and inconsistent values
      const fallbackPrice = 0.51; // Default conservative fallback
      const effectivePrice = price !== null ? price : fallbackPrice;
      
      // Use high precision multiplication
      const result = amount * effectivePrice;
      
      // Only log conversions for significant amounts to reduce console spam
      if (amount > 100) {
        console.log(`Converting ${amount} XRP to USD at rate ${effectivePrice}: $${result}`);
      }
      
      // Ensure we don't return NaN
      return isNaN(result) ? 0 : result;
    } catch (e) {
      console.error('Error calculating USD value:', e, 'for amount:', xrpAmount);
      return 0;
    }
  };

  // Format the refresh time in a user-friendly way
  const formatRefreshTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 60000) {
      return 'just now';
    } else if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else {
      const hours = Math.floor(diff / 3600000);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
  };

  const handleNewTokensToggle = () => {
    const newShowNewTokens = !showNewTokens;
    
    // Set loading state immediately to prevent "no tokens found" message
    setLoading(true);
    setError('Loading new tokens...');
    
    // When toggling to show new tokens, always set to creation date sorting
    if (newShowNewTokens && sortBy !== 'createdAt') {
      setSortBy('createdAt');
      setSortType('desc');
    }
    
    // Also clear any filter text to show all new tokens
    if (filterText) {
      setFilterText('');
    }
    
    // Update the state after all other changes are queued
    // This will trigger the useEffect that calls fetchTokens
    setShowNewTokens(newShowNewTokens);
  };

  // Update handleManualRefresh
  const handleManualRefresh = async () => {
    await fetchTokens(true);
    setLastRefreshTime(Date.now());
    setError('Feed refreshed');
    setTimeout(() => setError(null), 2000);
  };

  // Add wallet analyzer functions
  const handleOpenAnalyzer = () => {
    setShowAnalyzerModal(true);
  };
  
  const handleCloseAnalyzer = () => {
    setShowAnalyzerModal(false);
    setWalletAddress('');
    setAnalysisError(null);
    setReport(null);
    setShowReport(false);
    setAnalysisComplete(false);
  };

  const handleAnalyzerSubmit = async (e) => {
    e.preventDefault();
    setReport(null);
    setAnalysisError(null);
    setIsAnalyzing(true);
    setLoadingProgress({ loaded: 0, total: 100, status: 'Starting analysis...' });
    
    try {
      // Validate XRPL address format
      if (!walletAddress.match(/^r[1-9A-HJ-NP-Za-km-z]{25,34}$/)) {
        throw new Error('Invalid XRP wallet address format');
      }
      
      console.log(`Analyzing wallet ${walletAddress}`);
      
      // Try normal mode first
      try {
        const html = await analyzeWallet(walletAddress, false);
        setReport(html);
        setShowReport(true);
      } catch (initialError) {
        console.error("Initial analysis attempt failed:", initialError);
        
        // If rate limiting error, retry with conservative settings
        if (initialError.message && initialError.message.includes("too much load")) {
          setLoadingProgress(prev => ({ 
            ...prev, 
            status: 'Rate limit detected. Waiting 5 seconds before retrying with conservative settings...' 
          }));
          
          // Wait 5 seconds before retry
          await sleep(5000);
          
          // Retry with conservative mode
          const html = await analyzeWallet(walletAddress, true);
          setReport(html);
          setShowReport(true);
        }
        // Handle specific error cases with friendly messages
        else if (initialError.message.includes("actNotFound") || initialError.message.includes("Account does not exist")) {
          throw new Error(`The wallet address ${walletAddress} was not found on the XRP Ledger.`);
        }
        else if (initialError.message.includes("connect")) {
          throw new Error(`Unable to connect to the XRP Ledger. Please check your internet connection and try again.`);
        }
        else {
          // Re-throw other errors
          throw initialError;
        }
      }
    } catch (error) {
      console.error('Wallet analysis error:', error);
      // Set user-friendly error messages
      if (error.message.includes('Invalid XRP wallet address')) {
        setAnalysisError('Please enter a valid XRP wallet address starting with "r"');
      } else if (error.message.includes('not found')) {
        setAnalysisError(`Wallet address not found on the XRP Ledger. Please check the address and try again.`);
      } else if (error.message.includes('connect')) {
        setAnalysisError('Connection to XRPL failed. Please check your internet connection and try again.');
      } else if (error.message.includes('too much load')) {
        setAnalysisError('The XRPL server is currently experiencing high load. Please try again in a few minutes.');
      } else {
        setAnalysisError(`Analysis failed: ${error.message}`);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Function to run wallet analysis
  const analyzeWallet = async (address, conservativeMode = false) => {
    console.log(`Starting wallet analysis for ${address} (Conservative mode: ${conservativeMode})`);
    setLoadingProgress({ loaded: 0, total: 100, status: 'Connecting to XRPL...' });
    
    // Connect to XRP Ledger
    try {
      console.log('Fetching account info...');
      setLoadingProgress({ loaded: 10, total: 100, status: 'Fetching account details...' });
      
      // Get account info
      const accountInfo = await fetchAccountInfo(address);
      console.log('Account info:', accountInfo);
      
      if (!accountInfo) {
        throw new Error('Failed to retrieve account information');
      }
      
      // Get account transactions
      console.log('Fetching transactions...');
      setLoadingProgress({ loaded: 20, total: 100, status: 'Fetching transactions...' });
      
      let transactions = [];
      
      // Implement exponential backoff for transaction retrieval
      let attempt = 0;
      const maxAttempts = 5;
      
      while (attempt < maxAttempts) {
        try {
          const batchSize = conservativeMode ? 100 : 200;
          const delayBetweenBatches = conservativeMode ? 2000 : 1000;
          
          transactions = await fetchAccountTransactions(address, {
            batchSize,
            delayBetweenBatches,
            maxTransactions: conservativeMode ? 500 : 1000
          });
          
          console.log(`Successfully fetched ${transactions.length} transactions`);
          break;
        } catch (error) {
          attempt++;
          
          if (error.message.includes("too much load")) {
            const backoffTime = Math.pow(2, attempt) * 1000;
            console.log(`Rate limit hit. Backing off for ${backoffTime}ms (attempt ${attempt}/${maxAttempts})`);
            setLoadingProgress(prev => ({ 
              ...prev, 
              status: `Rate limit detected. Backing off for ${backoffTime/1000}s (attempt ${attempt}/${maxAttempts})...` 
            }));
            
            await sleep(backoffTime);
            
            // Switch to conservative mode after first failure
            conservativeMode = true;
          } else if (attempt >= maxAttempts) {
            throw error;
          } else {
            console.error(`Error fetching transactions (attempt ${attempt}/${maxAttempts}):`, error);
            const backoffTime = Math.pow(2, attempt) * 500;
            await sleep(backoffTime);
          }
        }
      }
      
      if (transactions.length === 0) {
        throw new Error('No transactions found for this wallet');
      }
      
      setLoadingProgress({ loaded: 50, total: 100, status: 'Analyzing transactions...' });
      console.log('Analyzing transactions...');
      
      // Analyze transactions to find XRP movements and patterns
      const txAnalysis = analyzeTransactions(transactions, address);
      
      // Add account information to the analysis
      txAnalysis.balance = parseFloat(accountInfo.xrp_balance || 0);
      txAnalysis.ownerCount = accountInfo.owner_count || 0;
      txAnalysis.firstLedger = accountInfo.inception?.ledger_index || null;
      txAnalysis.accountCreated = accountInfo.inception?.date ? new Date(accountInfo.inception.date) : null;
      
      // Add transaction type statistics
      txAnalysis.transactionTypes = {};
      transactions.forEach(tx => {
        const txType = tx.tx?.TransactionType || tx.TransactionType || 'Unknown';
        txAnalysis.transactionTypes[txType] = (txAnalysis.transactionTypes[txType] || 0) + 1;
      });
      
      // Generate HTML report
      setLoadingProgress({ loaded: 80, total: 100, status: 'Generating report...' });
      console.log('Generating report...');
      const htmlReport = generateHTMLReport(txAnalysis);
      
      setLoadingProgress({ loaded: 100, total: 100, status: 'Analysis complete!' });
      setAnalysisComplete(true);
      console.log('Analysis complete!');
      return htmlReport;
    } catch (error) {
      console.error('Analysis error:', error);
      throw error;
    }
  };

  // Function to fetch account transactions with exponential backoff and rate limiting
  const fetchAccountTransactions = async (address, options = {}) => {
    const {
      batchSize = 200,
      delayBetweenBatches = 1000,
      maxTransactions = 1000
    } = options;
    
    console.log(`Fetching transactions with batchSize=${batchSize}, delay=${delayBetweenBatches}ms, max=${maxTransactions}`);
    
    // Define XRPL endpoints to try in order of preference
    const endpoints = [
      'https://xrplcluster.com',
      'https://s1.ripple.com',
      'https://s2.ripple.com',
      'https://xrpl.ws',
      'https://xrpl.link'
    ];
    
    let transactions = [];
    let lastError = null;
    
    // Try each endpoint until one succeeds
    for (const endpoint of endpoints) {
      console.log(`Trying endpoint: ${endpoint}`);
      let endpointSuccess = false;
      
      try {
        let marker = undefined;
        
        do {
          try {
            // Update progress indication
            setLoadingProgress(prev => ({
              ...prev,
              loaded: Math.min(80, 20 + Math.floor((transactions.length / maxTransactions) * 60)),
              status: `Fetched ${transactions.length} transactions${marker ? ' (loading more)' : ''}...`
            }));
            
            // Prepare request payload
            const payload = {
              method: 'account_tx',
              params: [{
                account: address,
                ledger_index_min: -1,
                ledger_index_max: -1,
                limit: batchSize,
                binary: false
              }]
            };
            
            // Add marker if we have one
            if (marker) {
              payload.params[0].marker = marker;
            }
            
            // Make request to XRPL
            console.log(`Requesting transactions from ${endpoint}${marker ? ' with marker' : ''}`);
            const response = await axios.post(endpoint, payload, {
              timeout: 15000,
              headers: { 'Content-Type': 'application/json' }
            });
            
            // Check if we got a valid response
            if (response.data && response.data.result && response.data.result.transactions) {
              const newTxs = response.data.result.transactions;
              endpointSuccess = true;
              
              // Reset failure count on success
              rateLimit.resetFailures(endpoint);
              
              // Process transactions
              if (newTxs.length > 0) {
                transactions.push(...newTxs);
                console.log(`Added ${newTxs.length} transactions (total: ${transactions.length})`);
                
                // Check if we have a marker for pagination
                if (response.data.result.marker) {
                  marker = response.data.result.marker;
                  
                  // Stop if we've reached our transaction limit
                  if (transactions.length >= maxTransactions) {
                    console.log(`Reached maximum transaction count (${maxTransactions}), stopping pagination`);
                    marker = undefined;
                  }
                  
                  // Add delay between batches to reduce server load
                  if (marker) {
                    console.log(`Waiting ${delayBetweenBatches}ms before next batch...`);
                    await sleep(delayBetweenBatches);
                  }
                } else {
                  console.log('No more transactions (no marker)');
                  marker = undefined;
                }
              } else {
                console.log('No transactions in this batch');
                marker = undefined;
              }
            } else {
              console.log('No transactions found or invalid response format');
              marker = undefined; // Stop pagination
            }
          } catch (error) {
            console.error(`Pagination error: ${error.message}`);
            
            // Check for rate limit errors and back off
            if (error.message.includes("too much load")) {
              console.log(`Rate limit detected for ${endpoint}, implementing backoff...`);
              setLoadingProgress(prev => ({
                ...prev,
                status: `Rate limit detected. Implementing adaptive backoff...`
              }));
              
              // Use the enhanced backoff mechanism
              await rateLimit.backoff(endpoint);
              
              // Don't reset the marker - we'll retry this batch
            } else {
              // For other errors, stop pagination
              marker = undefined;
            }
          }
        } while (marker && transactions.length < maxTransactions);
        
        // If we successfully got transactions from this endpoint, we can stop
        if (endpointSuccess) {
          console.log(`Successfully fetched ${transactions.length} transactions via HTTP from ${endpoint}`);
          
          // Fetch detailed transaction info for a subset of transactions
          if (transactions.length > 0) {
            console.log('Fetching detailed transaction information...');
            
            // Process only a subset of transactions if there are many (to avoid timeout)
            const MAX_DETAILED_TXS = Math.min(100, Math.ceil(maxTransactions / 10));
            const txsToFetch = transactions.length > MAX_DETAILED_TXS ? 
              transactions.slice(0, MAX_DETAILED_TXS) : 
              transactions;
            
            if (transactions.length > MAX_DETAILED_TXS) {
              console.log(`Only fetching details for the ${MAX_DETAILED_TXS} most recent transactions`);
            }
            
            let counter = 0;
            let rateHits = 0; // Track rate limit hits during detail fetching
            
            for (const tx of txsToFetch) {
              const hash = tx.tx?.hash || tx.transaction?.hash || tx.hash;
              if (hash) {
                try {
                  // Add adaptive delay between requests based on rate limit hits
                  const requestDelay = rateHits === 0 ? 200 : // Normal operation
                                       rateHits === 1 ? 1000 : // Mild rate limiting
                                       rateHits >= 2 ? 2000 : 1000; // Heavy rate limiting
                                       
                  if (counter > 0) {
                    await sleep(requestDelay);
                  }
                  
                  // Get detailed transaction by hash
                  const detailResponse = await axios.post(endpoint, {
                    method: 'tx',
                    params: [{ transaction: hash }]
                  }, {
                    timeout: 5000,
                    headers: { 'Content-Type': 'application/json' }
                  });
                  
                  // Merge detailed info back into original transaction
                  if (detailResponse.data && detailResponse.data.result) {
                    // Update tx with more detailed information if available
                    if (tx.tx) {
                      tx.tx = { ...tx.tx, ...detailResponse.data.result };
                    } else if (tx.transaction) {
                      tx.transaction = { ...tx.transaction, ...detailResponse.data.result };
                    } else {
                      // Direct assignment for simpler structures
                      Object.assign(tx, detailResponse.data.result);
                    }
                  }
                  
                  // Reset rate hits counter on success
                  if (counter % 5 === 0) {
                    rateHits = Math.max(0, rateHits - 1);
                  }
                  
                  counter++;
                  if (counter % 10 === 0) {
                    console.log(`Fetched details for ${counter}/${txsToFetch.length} transactions...`);
                    
                    // Update progress
                    setLoadingProgress(prev => ({
                      ...prev,
                      loaded: Math.min(80, 20 + Math.floor((transactions.length / maxTransactions) * 50) + 
                        Math.floor((counter / txsToFetch.length) * 10)),
                      status: `Fetching transaction details (${counter}/${txsToFetch.length})...`
                    }));
                  }
                } catch (err) {
                  console.log(`Couldn't fetch details for transaction ${hash}: ${err.message}`);
                  
                  // If rate limited, increase the rate hits counter and pause
                  if (err.message.includes("too much load")) {
                    rateHits++;
                    const backoffTime = Math.min(1000 * Math.pow(2, rateHits), 10000);
                    console.log(`Rate limit hit ${rateHits} times. Backing off for ${backoffTime}ms`);
                    await sleep(backoffTime);
                  }
                }
              }
            }
          }
          
          // Return the transactions we successfully fetched
          return transactions;
        }
      } catch (error) {
        console.error(`HTTP endpoint ${endpoint} failed completely: ${error.message}`);
        lastError = error;
        // Try the next endpoint
      }
    }
    
    // If we get here, all endpoints failed
    if (transactions.length > 0) {
      console.log(`Returning ${transactions.length} transactions we managed to fetch despite errors`);
      return transactions;
    }
    
    throw new Error(`Failed to fetch transactions from any HTTP endpoint: ${lastError?.message || 'Unknown error'}`);
  };

  // Helper function to fetch account information
  const fetchAccountInfo = async (address) => {
    // Define XRPL endpoints to try in order of preference
    const endpoints = [
      'https://xrplcluster.com',
      'https://s1.ripple.com',
      'https://s2.ripple.com',
      'https://xrpl.ws',
      'https://xrpl.link'
    ];
    
    let lastError = null;
    
    // Try each endpoint until one succeeds
    for (const endpoint of endpoints) {
      try {
        console.log(`Fetching account info from ${endpoint}`);
        
        // Make request to XRPL
        const response = await axios.post(endpoint, {
          method: 'account_info',
          params: [{
            account: address,
            ledger_index: 'validated',
            strict: true
          }]
        }, {
          timeout: 10000,
          headers: { 'Content-Type': 'application/json' }
        });
        
        // Check if we got a valid response
        if (response.data && response.data.result && response.data.result.account_data) {
          console.log('Account info retrieved successfully');
          
          // Extract and return relevant account data
          const accountData = response.data.result.account_data;
          
          // Get creation info using account_tx with limit=1 and earliest ledgers
          try {
            const inceptionResponse = await axios.post(endpoint, {
              method: 'account_tx',
              params: [{
                account: address,
                ledger_index_min: 0,
                ledger_index_max: -1,
                limit: 1,
                binary: false
              }]
            }, {
              timeout: 10000,
              headers: { 'Content-Type': 'application/json' }
            });
            
            let inception = null;
            if (inceptionResponse.data?.result?.transactions?.length > 0) {
              const firstTx = inceptionResponse.data.result.transactions[0];
              const ledgerIndex = firstTx.tx?.ledger_index || firstTx.ledger_index;
              const date = firstTx.tx?.date || firstTx.date;
              
              if (ledgerIndex && date) {
                inception = {
                  ledger_index: ledgerIndex,
                  date: rippleTimeToDate(date)
                };
              }
            }
            
            return {
              address: accountData.Account,
              xrp_balance: dropsToXrp(accountData.Balance),
              sequence: accountData.Sequence,
              owner_count: accountData.OwnerCount,
              inception
            };
          } catch (e) {
            console.warn('Could not fetch account inception data:', e.message);
            
            // Return what we have even without inception data
            return {
              address: accountData.Account,
              xrp_balance: dropsToXrp(accountData.Balance),
              sequence: accountData.Sequence,
              owner_count: accountData.OwnerCount
            };
          }
        }
      } catch (error) {
        console.error(`Failed to fetch account info from ${endpoint}: ${error.message}`);
        lastError = error;
        // Try the next endpoint
      }
    }
    
    // If we get here, all endpoints failed
    throw new Error(`Failed to fetch account info from any endpoint: ${lastError?.message || 'Unknown error'}`);
  };

  // Analyze transactions to find XRP movements and patterns
  const analyzeTransactions = (transactions, walletAddress) => {
    // Known exchanges/services for wallet identification (from xrpTxAnalyzer.js)
    const KNOWN_WALLETS = {
      'rLHzPsX6oXkzU2qL12kHCH8G8cnZv1rBJh': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
      'rU6K7V3Po4snVhBBaU29sesqs2qTQJWDw1': { name: 'Binance Cold Wallet', type: 'exchange', color: '#F0B90B' },
      'rEb8TK3gBgk5auZkwc6sHnwrGVJH8DuaLh': { name: 'Binance Hot Wallet', type: 'exchange', color: '#F0B90B' },
      'rJb5KsHsDHF1YS5B5DU6QCkH5NsPaKQTcy': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
      'rBWpYJhuJWBPAkzJ4kYQqHShSkkF3rgeD': { name: 'Bitrue', type: 'exchange', color: '#2ED3D2' },
      'rNGkUxPVuXQiUV1Qmam2NoXykdvk7KKuGa': { name: 'Uphold', type: 'exchange', color: '#7734BE' },
      'rMQ98K56yXJbDGv49ZSmW51sLn94Xe1mu1': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
      'rw2ciyaNshpHe7bCHo4bRWq6pqqynnWKQg': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
      'rPVMhWBsfF9iMXYj3aAzJVkPDTFNSyWdKy': { name: 'Bittrex', type: 'exchange', color: '#1C2226' },
      'rG6FZ31hDHN1K5Dkbma3PSB5uVCuVVRzfn': { name: 'Bitso', type: 'exchange', color: '#FF0151' },
      'rLkMJhSVwhmummLjJPVrfPZVdrjYZokQjy': { name: 'Bitstamp', type: 'exchange', color: '#1B97E9' },
      'rJQi8gFZafF3eiXRJ9o9mxsrz8u9MfcJkk': { name: 'BitGo', type: 'exchange', color: '#022C4F' },
      'rUzWJkXyqnF8IAhSQbdBtDLLnrXu5j4Hln': { name: 'Bithumb', type: 'exchange', color: '#FB4319' },
      'rK59hSXoeaJxzWb8vQKyPpgrX69GpGFCzn': { name: 'Kraken', type: 'exchange', color: '#5741D9' },
      'rNxp4h8apvRis6mJf9S1hithQWFHrCXgVg': { name: 'CoinOne', type: 'exchange', color: '#3FD0AE' },
      'r9HwsqBnAUN4nF6nDqxd4sgP8DrDnDcZP3': { name: 'XRPL Labs', type: 'service', color: '#1E90FF' },
    };

    // Organize our data structure like in the original analyzer
    const result = {
      address: walletAddress,
      xrpIn: [],  // Transactions sending XRP to this address
      xrpOut: [], // Transactions sending XRP from this address
      totalXrpIn: 0,
      totalXrpOut: 0,
      txTypes: {},
      tokenInteractions: {},
      txByDate: {},
      senderSummary: {},  // Summary by sender address
      receiverSummary: {} // Summary by recipient address
    };

    if (!transactions || transactions.length === 0) {
      console.log('No transactions to analyze');
      return result;
    }

    console.log('Analyzing transactions for XRP movements...');
    let xrpInCount = 0;
    let xrpOutCount = 0;
    
    // Log details about the first transaction to understand the structure (as in original)
    console.log('\nExamining transaction structure for debugging:');
    const firstTx = transactions[0];
    if (firstTx) {
      console.log(`Transaction structure keys: ${Object.keys(firstTx).join(', ')}`);
      
      // Check the transaction structure to help with debugging
      if (firstTx.tx_json) {
        console.log(`tx_json exists with keys: ${Object.keys(firstTx.tx_json).join(', ')}`);
        console.log(`TransactionType: ${firstTx.tx_json.TransactionType}`);
        console.log(`Account: ${firstTx.tx_json.Account}`);
        if (firstTx.tx_json.Destination) {
          console.log(`Destination: ${firstTx.tx_json.Destination}`);
        }
        
        // Show all amount fields to understand structure
        if (firstTx.tx_json.Amount) {
          console.log(`Amount: ${firstTx.tx_json.Amount} (${typeof firstTx.tx_json.Amount})`);
        } else if (firstTx.tx_json.DeliverMax) {
          console.log(`DeliverMax: ${firstTx.tx_json.DeliverMax} (${typeof firstTx.tx_json.DeliverMax})`);
        } else if (firstTx.tx_json.SendMax) {
          console.log(`SendMax: ${firstTx.tx_json.SendMax} (${typeof firstTx.tx_json.SendMax})`);
        }
      }
    }
    
    // Process each transaction
    for (const tx of transactions) {
      // Get transaction data - handle different property names from API responses
      const txData = tx.tx_json || tx.tx || tx.transaction || {};
      const metaData = tx.meta || tx.metaData || {};
      
      // Skip if no txData or transaction failed
      if (!txData || metaData.TransactionResult !== 'tesSUCCESS') {
        continue;
      }

      // Count transaction types for statistics
      const txType = txData.TransactionType || 'Unknown';
      result.txTypes[txType] = (result.txTypes[txType] || 0) + 1;
      
      // Track transaction dates for timeline analysis
      const txDate = txData.date ? rippleTimeToDate(txData.date) : 
                    (tx.close_time_human ? new Date(tx.close_time_human) : 
                    (tx.date ? new Date(tx.date) : new Date()));
      
      const monthYear = txDate.toISOString().substring(0, 7); // YYYY-MM format
      result.txByDate[monthYear] = (result.txByDate[monthYear] || 0) + 1;

      // Process XRP payments (following original logic)
      if (txType === 'Payment') {
        // Check for XRP transfer (Amount as a string for native XRP, object for tokens)
        let isNativeXrp = false;
        let amount = 0;
        
        // Check for direct Amount (standard XRP payment)
        if (typeof txData.Amount === 'string') {
          isNativeXrp = true;
          amount = parseFloat(txData.Amount) / 1000000; // Convert drops to XRP
        }
        // Check for DeliverMax (newer API format)
        else if (typeof txData.DeliverMax === 'string') {
          isNativeXrp = true;
          amount = parseFloat(txData.DeliverMax) / 1000000;
        }
        // Check for SendMax (another format for partial payments)
        else if (typeof txData.SendMax === 'string') {
          isNativeXrp = true;
          amount = parseFloat(txData.SendMax) / 1000000;
        }
        
        if (!isNativeXrp) {
          continue; // Skip token transfers
        }
        
        // Get destination tag if it exists
        const destinationTag = txData.DestinationTag || null;
        
        // Get source tag if it exists
        const sourceTag = txData.SourceTag || null;
        
        // Extract memos if they exist
        const memos = txData.Memos ? extractMemos(txData.Memos) : [];
        
        // Check if this is a memo we should note
        if (memos.length > 0) {
          console.log(`Memo found: ${memos[0].data}`);
        }
        
        // Format transaction date consistently
        const formattedDate = txDate instanceof Date ? txDate : new Date(txDate);
        
        // XRP coming in to our wallet
        if (txData.Destination === walletAddress && txData.Account !== walletAddress) {
          const sender = txData.Account;
          result.totalXrpIn += amount;
          xrpInCount++;
          
          // Add transaction details
          result.xrpIn.push({
            txId: txData.hash || tx.hash,
            date: formattedDate,
            sender: sender,
            amount: amount,
            sourceTag: sourceTag,
            destinationTag: destinationTag,
            memos: memos,
            ledgerIndex: tx.ledger_index || txData.ledger_index,
            fee: txData.Fee ? parseFloat(txData.Fee) / 1000000 : null
          });
          
          // Update sender summary
          if (!result.senderSummary[sender]) {
            result.senderSummary[sender] = { 
              totalAmount: 0,                 // Changed from total to totalAmount
              transactionCount: 0,            // Changed from count to transactionCount
              name: KNOWN_WALLETS[sender]?.name || null,
              type: KNOWN_WALLETS[sender]?.type || null,
              color: KNOWN_WALLETS[sender]?.color || null,
              hasTags: false,
              hasMemos: false,
              transactions: []
            };
          }
          
          result.senderSummary[sender].totalAmount += amount;  // Changed from total to totalAmount
          result.senderSummary[sender].transactionCount++;     // Changed from count to transactionCount
          result.senderSummary[sender].hasTags = result.senderSummary[sender].hasTags || (sourceTag !== null || destinationTag !== null);
          result.senderSummary[sender].hasMemos = result.senderSummary[sender].hasMemos || (memos.length > 0);
          result.senderSummary[sender].transactions.push(txData.hash || tx.hash);
        } 
        
        // XRP going out from our wallet
        if (txData.Account === walletAddress && txData.Destination !== walletAddress) {
          const recipient = txData.Destination;
          result.totalXrpOut += amount;
          xrpOutCount++;
          
          // Add transaction details
          result.xrpOut.push({
            txId: txData.hash || tx.hash,
            date: formattedDate,
            recipient: recipient,
            amount: amount,
            sourceTag: sourceTag,
            destinationTag: destinationTag,
            memos: memos,
            ledgerIndex: tx.ledger_index || txData.ledger_index,
            fee: txData.Fee ? parseFloat(txData.Fee) / 1000000 : null
          });
          
          // Update recipient summary
          if (!result.receiverSummary[recipient]) {
            result.receiverSummary[recipient] = { 
              totalAmount: 0,                      // Changed from total to totalAmount
              transactionCount: 0,                  // Changed from count to transactionCount
              name: KNOWN_WALLETS[recipient]?.name || null,
              type: KNOWN_WALLETS[recipient]?.type || null,
              color: KNOWN_WALLETS[recipient]?.color || null,
              hasTags: false,
              hasMemos: false,
              transactions: []
            };
          }
          
          result.receiverSummary[recipient].totalAmount += amount;  // Changed from total to totalAmount
          result.receiverSummary[recipient].transactionCount++;     // Changed from count to transactionCount
          result.receiverSummary[recipient].hasTags = result.receiverSummary[recipient].hasTags || (sourceTag !== null || destinationTag !== null);
          result.receiverSummary[recipient].hasMemos = result.receiverSummary[recipient].hasMemos || (memos.length > 0);
          result.receiverSummary[recipient].transactions.push(txData.hash || tx.hash);
        }
      }
      
      // Track token offers and DEX activity (AMMs, OfferCreate, etc.)
      if (txType === 'OfferCreate' || txType === 'OfferCancel' || txType === 'TrustSet' || 
          txType === 'AMMCreate' || txType === 'AMMDeposit' || txType === 'AMMWithdraw') {
        // Add to token interactions tracking
        const counterparty = txData.Destination || txData.Account;
        if (counterparty && counterparty !== walletAddress) {
          result.tokenInteractions[counterparty] = (result.tokenInteractions[counterparty] || 0) + 1;
        }
      }
    }
    
    console.log(`Found ${xrpInCount} incoming and ${xrpOutCount} outgoing XRP transactions`);
    return result;
  };

  // Helper function to extract memos from a transaction (like in the original)
  const extractMemos = (memos) => {
    if (!memos || !Array.isArray(memos)) {
      return [];
    }
    
    return memos.map(memo => {
      const data = memo.Memo.MemoData ? hexToString(memo.Memo.MemoData) : '';
      const type = memo.Memo.MemoType ? hexToString(memo.Memo.MemoType) : '';
      const format = memo.Memo.MemoFormat ? hexToString(memo.Memo.MemoFormat) : '';
      
      return { data, type, format };
    });
  };

  // Infer wallet type based on transaction patterns (like in the original)
  const inferWalletType = (address, data, analysis) => {
    // Default unknown wallet
    const result = { 
      name: 'Unknown', 
      type: 'unknown', 
      color: '#999999'
    };
    
    // Check if it's a bot/fee wallet
    if (data.transactionCount > 50 && data.totalAmount / data.transactionCount < 0.5) {
      result.name = 'Bot/Fee Wallet';
      result.type = 'bot';
      result.color = '#e74c3c';
      return result;
    }
    
    // Check if it might be an exchange
    if (data.hasTags && data.transactionCount > 5 && analysis) {
      const tagCounts = {};
      
      // Collect tag usage patterns
      if (analysis.xrpIn && analysis.xrpIn.some(tx => tx.sender === address)) {
        analysis.xrpIn
          .filter(tx => tx.sender === address && tx.destinationTag !== null)
          .forEach(tx => {
            tagCounts[tx.destinationTag] = (tagCounts[tx.destinationTag] || 0) + 1;
          });
      }
      
      if (analysis.xrpOut && analysis.xrpOut.some(tx => tx.recipient === address)) {
        analysis.xrpOut
          .filter(tx => tx.recipient === address && tx.destinationTag !== null)
          .forEach(tx => {
            tagCounts[tx.destinationTag] = (tagCounts[tx.destinationTag] || 0) + 1;
          });
      }
      
      // If there's consistent tag usage, it might be an exchange
      const tagEntries = Object.entries(tagCounts);
      if (tagEntries.length > 0) {
        const mostUsedTagCount = Math.max(...tagEntries.map(e => e[1]));
        const totalTagged = tagEntries.reduce((sum, e) => sum + e[1], 0);
        
        if (mostUsedTagCount / totalTagged > 0.7) {
          result.name = 'Likely Exchange';
          result.type = 'exchange';
          result.color = '#3498db';
          return result;
        }
      }
    }
    
    // Large transactions wallet
    if (data.totalAmount > 1000 && data.transactionCount < 10) {
      result.name = 'Large Value Wallet';
      result.type = 'wealth';
      result.color = '#f39c12';
      return result;
    }
    
    // Regular usage pattern
    if (data.transactionCount > 10 && data.transactionCount < 50 &&
        data.totalAmount / data.transactionCount > 5 && 
        data.totalAmount / data.transactionCount < 100) {
      result.name = 'Regular User';
      result.type = 'user';
      result.color = '#2ecc71';
      return result;
    }
    
    return result;
  };

  // Generate HTML report from analysis data
  const generateHTMLReport = (analysis) => {
    try {
      // Make sure we have a valid analysis object with required properties
      if (!analysis || typeof analysis !== 'object') {
        throw new Error('Invalid analysis data');
      }
      
      // Initialize required properties with default values if they don't exist
      analysis.address = analysis.address || 'Unknown';
      analysis.xrpIn = analysis.xrpIn || [];
      analysis.xrpOut = analysis.xrpOut || [];
      analysis.totalXrpIn = typeof analysis.totalXrpIn === 'number' ? analysis.totalXrpIn : 0;
      analysis.totalXrpOut = typeof analysis.totalXrpOut === 'number' ? analysis.totalXrpOut : 0;
      analysis.senderSummary = analysis.senderSummary || {};
      analysis.receiverSummary = analysis.receiverSummary || {};
      
      // Use the existing calculateUsd function instead of redefining getUsdValue
      const getUsdValue = calculateUsd;

      // Format USD display with proper currency symbol and precision
      const formatUsd = (usdValue) => {
        if (usdValue === null) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(usdValue);
      };

      // Known exchange/service wallets database
      const KNOWN_WALLETS = {
        'rLHzPsX6oXkzU2qL12kHCH8G8cnZv1rBJh': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
        'rU6K7V3Po4snVhBBaU29sesqs2qTQJWDw1': { name: 'Binance Cold Wallet', type: 'exchange', color: '#F0B90B' },
        'rEb8TK3gBgk5auZkwc6sHnwrGVJH8DuaLh': { name: 'Binance Hot Wallet', type: 'exchange', color: '#F0B90B' },
        'rJb5KsHsDHF1YS5B5DU6QCkH5NsPaKQTcy': { name: 'Binance', type: 'exchange', color: '#F0B90B' },
        'rBWpYJhuJWBPAkzJ4kYQqHShSkkF3rgeD': { name: 'Bitrue', type: 'exchange', color: '#2ED3D2' },
        'rNGkUxPVuXQiUV1Qmam2NoXykdvk7KKuGa': { name: 'Uphold', type: 'exchange', color: '#7734BE' },
        'rMQ98K56yXJbDGv49ZSmW51sLn94Xe1mu1': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
        'rw2ciyaNshpHe7bCHo4bRWq6pqqynnWKQg': { name: 'Coinbase', type: 'exchange', color: '#0052FF' },
        'rPVMhWBsfF9iMXYj3aAzJVkPDTFNSyWdKy': { name: 'Bittrex', type: 'exchange', color: '#1C2226' },
        'rG6FZ31hDHN1K5Dkbma3PSB5uVCuVVRzfn': { name: 'Bitso', type: 'exchange', color: '#FF0151' },
        'rLkMJhSVwhmummLjJPVrfPZVdrjYZokQjy': { name: 'Bitstamp', type: 'exchange', color: '#1B97E9' },
        'rJQi8gFZafF3eiXRJ9o9mxsrz8u9MfcJkk': { name: 'BitGo', type: 'exchange', color: '#022C4F' },
        'rUzWJkXyqnF8IAhSQbdBtDLLnrXu5j4Hln': { name: 'Bithumb', type: 'exchange', color: '#FB4319' },
        'rK59hSXoeaJxzWb8vQKyPpgrX69GpGFCzn': { name: 'Kraken', type: 'exchange', color: '#5741D9' },
        'rNxp4h8apvRis6mJf9S1hithQWFHrCXgVg': { name: 'CoinOne', type: 'exchange', color: '#3FD0AE' },
        'r9HwsqBnAUN4nF6nDqxd4sgP8DrDnDcZP3': { name: 'XRPL Labs', type: 'service', color: '#1E90FF' },
      };

      // Identify main deposit wallet (highest frequency incoming)
      const mainDepositWallets = Object.entries(analysis.senderSummary)
        .sort((a, b) => b[1].totalAmount - a[1].totalAmount)
        .filter(([_, data]) => data.totalAmount > 0)
        .slice(0, 5);

      // Check if there's a clear main deposit wallet
      let mainDepositWallet = null;
      if (mainDepositWallets.length > 0) {
        // If the top wallet has significantly more transaction value than others
        const [topAddress, topData] = mainDepositWallets[0];
        if (mainDepositWallets.length === 1 || 
            (mainDepositWallets.length > 1 && topData.totalAmount > mainDepositWallets[1][1].totalAmount * 1.5)) {
          mainDepositWallet = { 
            address: topAddress, 
            name: KNOWN_WALLETS[topAddress]?.name || 'Unknown',
            totalAmount: topData.totalAmount,
            transactionCount: topData.transactionCount 
          };
        }
      }

      // Prepare wallet interaction data for rich display
      const walletInteractions = {};
      
      // Add sender wallets
      Object.entries(analysis.senderSummary).forEach(([address, data]) => {
        if (!walletInteractions[address]) {
          walletInteractions[address] = {
            address,
            incomingCount: data.transactionCount,
            incomingAmount: data.totalAmount,
            outgoingCount: 0,
            outgoingAmount: 0,
            transactionCount: data.transactionCount,
            totalAmount: data.totalAmount,
            walletInfo: KNOWN_WALLETS[address] || inferWalletType(address, data)
          };
        }
      });
      
      // Add recipient wallets
      Object.entries(analysis.receiverSummary).forEach(([address, data]) => {
        if (!walletInteractions[address]) {
          walletInteractions[address] = {
            address,
            incomingCount: 0,
            incomingAmount: 0,
            outgoingCount: data.transactionCount,
            outgoingAmount: data.totalAmount,
            transactionCount: data.transactionCount,
            totalAmount: data.totalAmount,
            walletInfo: KNOWN_WALLETS[address] || inferWalletType(address, data)
          };
        } else {
          walletInteractions[address].outgoingCount = data.transactionCount;
          walletInteractions[address].outgoingAmount = data.totalAmount;
          walletInteractions[address].transactionCount += data.transactionCount;
          walletInteractions[address].totalAmount += data.totalAmount;
        }
      });
      
      // Rank wallets by total interaction amount
      const rankedWallets = Object.values(walletInteractions)
        .sort((a, b) => b.totalAmount - a.totalAmount);
      
      // Classify bot/fee wallets (many small transactions)
      const botFeeWallets = rankedWallets
        .filter(w => w.transactionCount > 50 && w.totalAmount / w.transactionCount < 0.5)
        .slice(0, 5);
      
      // Analyze destination tags - Group tags by recipient wallet
      const destinationTagsByWallet = {};
      
      analysis.xrpOut
        .filter(tx => tx.destinationTag !== null)
        .forEach(tx => {
          if (!destinationTagsByWallet[tx.recipient]) {
            destinationTagsByWallet[tx.recipient] = {
              tags: {}
            };
          }
          
          if (!destinationTagsByWallet[tx.recipient].tags[tx.destinationTag]) {
            destinationTagsByWallet[tx.recipient].tags[tx.destinationTag] = {
              count: 0,
              totalAmount: 0
            };
          }
          
          destinationTagsByWallet[tx.recipient].tags[tx.destinationTag].count++;
          destinationTagsByWallet[tx.recipient].tags[tx.destinationTag].totalAmount += tx.amount;
        });
      
      // For each wallet, find the most used tag
      Object.keys(destinationTagsByWallet).forEach(wallet => {
        const tagEntries = Object.entries(destinationTagsByWallet[wallet].tags);
        
        if (tagEntries.length > 0) {
          // Sort by usage count
          const sortedTags = tagEntries.sort((a, b) => b[1].count - a[1].count);
          destinationTagsByWallet[wallet].mostUsedTag = {
            tag: sortedTags[0][0],
            count: sortedTags[0][1].count,
            totalAmount: sortedTags[0][1].totalAmount
          };
          
          // Calculate consistency (% of transactions with most used tag)
          const totalTaggedTxs = tagEntries.reduce((sum, [_, data]) => sum + data.count, 0);
          destinationTagsByWallet[wallet].tagConsistency = sortedTags[0][1].count / totalTaggedTxs;
        }
      });
      
      // Get significant transactions (over 100 XRP, most recent first)
      const significantTransactions = [...analysis.xrpIn, ...analysis.xrpOut]
        .filter(tx => tx.amount > 100) 
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10);

      // Generate detailed HTML report
      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>XRP Wallet Analysis: ${analysis.address}</title>
          <style>
            * {
              box-sizing: border-box;
              font-family: Arial, sans-serif;
            }
            body {
              background-color: #f5f7fa;
              margin: 0;
              padding: 20px;
              color: #333;
            }
            .container {
              max-width: 1200px;
              margin: 0 auto;
              background-color: white;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              padding: 20px;
            }
            h1, h2, h3 {
              color: #333;
            }
            h1 {
              text-align: center;
              color: #0077cc;
              margin-bottom: 30px;
              padding-bottom: 10px;
              border-bottom: 2px solid #eee;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
              gap: 15px;
              margin-bottom: 30px;
            }
            .summary-card {
              background-color: #f9f9f9;
              border-radius: 8px;
              padding: 15px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.05);
            }
            .summary-card h3 {
              margin-top: 0;
              color: #0077cc;
              border-bottom: 1px solid #eee;
              padding-bottom: 8px;
            }
            .stat-container {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
            }
            .stat-label {
              font-weight: bold;
              color: #666;
            }
            .stat-value {
              text-align: right;
            }
            .highlight {
              color: #d35400;
              font-weight: bold;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            th {
              background-color: #0077cc;
              color: white;
              padding: 12px;
              text-align: left;
            }
            td {
              padding: 10px 12px;
              border-bottom: 1px solid #eee;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            tr:hover {
              background-color: #f0f4f8;
            }
            .wallet-address {
              font-family: monospace;
              font-size: 0.9em;
              color: #666;
            }
            .wallet-name {
              font-weight: bold;
            }
            .section {
              margin-bottom: 40px;
            }
            .tag-list {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
            }
            .tag {
              background-color: #eee;
              border-radius: 4px;
              padding: 5px 10px;
              font-size: 0.9em;
              display: inline-block;
            }
            .bot-fee-wallet {
              background-color: #ffe6e6 !important;
            }
            .exchange-wallet {
              background-color: #e6f7ff !important;
            }
            .main-deposit {
              background-color: #e6fff2 !important;
              font-weight: bold;
            }
            .warning {
              color: #e74c3c;
            }
            .wallet-badge {
              display: inline-block;
              padding: 3px 6px;
              border-radius: 4px;
              color: white;
              font-size: 0.8em;
              margin-left: 5px;
            }
            .risk-low {
              background-color: #2ecc71;
            }
            .risk-medium {
              background-color: #f39c12;
            }
            .risk-high {
              background-color: #e74c3c;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>XRP Wallet Analysis Report</h1>
            
            <div class="section">
              <h2>Wallet Overview</h2>
              <div class="summary-grid">
                <div class="summary-card">
                  <h3>Basic Information</h3>
                  <div class="stat-container">
                    <span class="stat-label">Address:</span>
                    <span class="stat-value wallet-address">${analysis.address}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Balance:</span>
                    <span class="stat-value">${analysis.accountInfo ? dropsToXrp(analysis.accountInfo.Balance) : 'N/A'} XRP</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Objects:</span>
                    <span class="stat-value">${analysis.accountObjects?.length || 0}</span>
                  </div>
                </div>
                
                <div class="summary-card">
                  <h3>Transaction Summary</h3>
                  <div class="stat-container">
                    <span class="stat-label">Total Received:</span>
                    <span class="stat-value highlight">${typeof analysis.totalXrpIn === 'number' ? analysis.totalXrpIn.toFixed(2) : '0.00'} XRP</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Total Sent:</span>
                    <span class="stat-value highlight">${typeof analysis.totalXrpOut === 'number' ? analysis.totalXrpOut.toFixed(2) : '0.00'} XRP</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Net Balance:</span>
                    <span class="stat-value">${typeof analysis.totalXrpIn === 'number' && typeof analysis.totalXrpOut === 'number' ? 
                      (analysis.totalXrpIn - analysis.totalXrpOut).toFixed(2) : '0.00'} XRP</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label"></span>
                    <span class="stat-value">${typeof analysis.totalXrpIn === 'number' ? formatUsd(getUsdValue(analysis.totalXrpIn)) : '$0.00'}</span>
                  </div>
                </div>
                
                <div class="summary-card">
                  <h3>Interaction Statistics</h3>
                  <div class="stat-container">
                    <span class="stat-label">Incoming Transactions:</span>
                    <span class="stat-value">${analysis.xrpIn.length}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Outgoing Transactions:</span>
                    <span class="stat-value">${analysis.xrpOut.length}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Unique Senders:</span>
                    <span class="stat-value">${Object.keys(analysis.senderSummary).length}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Unique Recipients:</span>
                    <span class="stat-value">${Object.keys(analysis.receiverSummary).length}</span>
                  </div>
                </div>
                
                ${mainDepositWallet ? `
                <div class="summary-card main-deposit">
                  <h3>Main Deposit Wallet</h3>
                  <div class="stat-container">
                    <span class="stat-label">Address:</span>
                    <span class="stat-value wallet-address">${mainDepositWallet.address || 'Unknown'}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Name:</span>
                    <span class="stat-value">${mainDepositWallet.name || 'Unknown'}</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Total Received:</span>
                    <span class="stat-value highlight">${mainDepositWallet.totalAmount ? mainDepositWallet.totalAmount.toFixed(2) : '0.00'} XRP</span>
                  </div>
                  <div class="stat-container">
                    <span class="stat-label">Transactions:</span>
                    <span class="stat-value">${mainDepositWallet.transactionCount || 0}</span>
                  </div>
                </div>` : ''}
              </div>
            </div>
            
            <div class="section">
              <h2>Top Wallets by Interaction Volume</h2>
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Wallet</th>
                    <th>Type</th>
                    <th>Incoming XRP</th>
                    <th>Outgoing XRP</th>
                    <th>Total XRP</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  ${(rankedWallets || []).slice(0, 15).map((wallet, index) => {
                    // Determine the wallet type and style
                    let rowClass = '';
                    let riskBadge = '';
                    
                    if (botFeeWallets.some(b => b.address === wallet.address)) {
                      rowClass = 'bot-fee-wallet';
                      riskBadge = '<span class="wallet-badge risk-high">BOT/FEE</span>';
                    } else if (wallet.walletInfo.type === 'exchange') {
                      rowClass = 'exchange-wallet';
                      riskBadge = '<span class="wallet-badge risk-low">EXCHANGE</span>';
                    }
                    
                    if (mainDepositWallet && wallet.address === mainDepositWallet.address) {
                      rowClass = 'main-deposit';
                      riskBadge += '<span class="wallet-badge risk-medium">MAIN DEPOSIT</span>';
                    }
                    
                    // Safely access wallet properties
                    const safeWalletInfo = wallet?.walletInfo || { name: 'Unknown', color: '#333', type: 'unknown' };
                    
                    // Create safe display string
                    const walletDisplay = (safeWalletInfo.name && safeWalletInfo.name !== 'Unknown') 
                      ? `<span class="wallet-name" style="color:${safeWalletInfo.color || '#333'}">${safeWalletInfo.name}</span><br>
                         <span class="wallet-address">${wallet?.address || 'Unknown'}</span>`
                      : `<span class="wallet-address">${wallet?.address || 'Unknown'}</span>`;
                    
                    // Safe number formatting
                    const incomingAmount = typeof wallet?.incomingAmount === 'number' ? wallet.incomingAmount.toFixed(2) : '0.00';
                    const outgoingAmount = typeof wallet?.outgoingAmount === 'number' ? wallet.outgoingAmount.toFixed(2) : '0.00';
                    const totalAmount = typeof wallet?.totalAmount === 'number' ? wallet.totalAmount.toFixed(2) : '0.00';
                    
                    return `
                    <tr class="${rowClass}">
                      <td>${index + 1}</td>
                      <td>${walletDisplay}</td>
                      <td>${safeWalletInfo.name} ${riskBadge}</td>
                      <td>${incomingAmount} XRP</td>
                      <td>${outgoingAmount} XRP</td>
                      <td><strong>${totalAmount} XRP</strong></td>
                      <td>${wallet?.transactionCount || 0}</td>
                    </tr>`;
                  }).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h2>Destination Tag Analysis</h2>
              <p>Destination tags are used by exchanges and services to identify customer accounts. Consistent tag usage indicates regular exchange deposits.</p>
              
              <table>
                <thead>
                  <tr>
                    <th>Wallet</th>
                    <th>Most Used Tag</th>
                    <th>Tag Consistency</th>
                    <th>Amount</th>
                    <th>Usage Count</th>
                  </tr>
                </thead>
                <tbody>
                  ${Object.entries(destinationTagsByWallet || {})
                    .filter(([_, data]) => data && data.mostUsedTag && data.mostUsedTag.count > 0)
                    .sort((a, b) => (b[1].tagConsistency || 0) - (a[1].tagConsistency || 0))
                    .slice(0, 10)
                    .map(([address, data]) => {
                      const walletInfo = KNOWN_WALLETS[address] || { name: 'Unknown', type: 'unknown', color: '#666' };
                      
                      return `
                      <tr>
                        <td>
                          <span class="wallet-name" style="color:${walletInfo.color || '#666'}">${walletInfo.name || 'Unknown'}</span><br>
                          <span class="wallet-address">${address}</span>
                        </td>
                        <td><strong>${data.mostUsedTag?.tag || 'N/A'}</strong></td>
                        <td>${data.tagConsistency !== undefined && data.tagConsistency !== null ? (data.tagConsistency * 100).toFixed(1) : '0.0'}%</td>
                        <td>${data.mostUsedTag?.totalAmount !== undefined && data.mostUsedTag?.totalAmount !== null ? data.mostUsedTag.totalAmount.toFixed(2) : '0.00'} XRP</td>
                        <td>${data.mostUsedTag?.count || 0} times</td>
                      </tr>`;
                    }).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h2>Recent Significant Transactions</h2>
              <p>Large value transactions that might indicate important account activity.</p>
              
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Counterparty</th>
                    <th>Tag</th>
                  </tr>
                </thead>
                <tbody>
                  ${[...(analysis.xrpIn || []), ...(analysis.xrpOut || [])]
                    .filter(tx => tx && typeof tx.amount === 'number' && tx.amount > 100) // Only significant amounts
                    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)) // Most recent first
                    .slice(0, 10) // Top 10
                    .map(tx => {
                    const isIncoming = 'sender' in tx;
                    const counterparty = isIncoming ? tx.sender : tx.recipient;
                    const walletInfo = KNOWN_WALLETS[counterparty] || { name: 'Unknown', type: 'unknown', color: '#666' };
                    const tag = tx.destinationTag;
                    
                    return `
                    <tr>
                      <td>${tx.date || 'Unknown date'}</td>
                      <td>${isIncoming ? 'Received' : 'Sent'}</td>
                      <td><strong>${tx.amount ? tx.amount.toFixed(2) : '0.00'} XRP</strong></td>
                      <td>
                        <span class="wallet-name" style="color:${walletInfo?.color || '#666'}">${walletInfo?.name || 'Unknown'}</span><br>
                        <span class="wallet-address">${counterparty || 'Unknown'}</span>
                      </td>
                      <td>${tag !== null ? tag : 'None'}</td>
                    </tr>`;
                  }).join('')}
                </tbody>
              </table>
            </div>
            
            ${botFeeWallets && botFeeWallets.length > 0 ? `
            <div class="section">
              <h2>Bot/Fee Wallets</h2>
              <p>These wallets show patterns typical of automated bots or fee payment wallets (many small transactions).</p>
              
              <table>
                <thead>
                  <tr>
                    <th>Wallet</th>
                    <th>Transactions</th>
                    <th>Total Amount</th>
                    <th>Avg Per Transaction</th>
                    <th>Pattern</th>
                  </tr>
                </thead>
                <tbody>
                  ${(botFeeWallets || []).map(wallet => {
                    return `
                    <tr class="bot-fee-wallet">
                      <td>
                        <span class="wallet-address">${wallet.address || 'Unknown'}</span>
                      </td>
                      <td>${wallet.transactionCount || 0}</td>
                      <td>${wallet.totalAmount ? wallet.totalAmount.toFixed(6) : '0.000000'} XRP</td>
                      <td>${wallet.totalAmount && wallet.transactionCount ? (wallet.totalAmount / wallet.transactionCount).toFixed(6) : '0.000000'} XRP</td>
                      <td>${wallet.transactionCount > 100 && wallet.totalAmount && wallet.totalAmount / wallet.transactionCount < 0.1 
                          ? '<span class="warning">High frequency, very low value</span>' 
                          : 'Low value transactions'}</td>
                    </tr>`;
                  }).join('')}
                </tbody>
              </table>
            </div>` : ''}
            
            <div class="section" style="text-align: center; padding: 20px; color: #888; font-size: 0.9em;">
              <p>Report generated ${new Date().toISOString()}</p>
              <p>XRP Ledger Analysis Tool</p>
            </div>
          </div>
        </body>
        </html>
      `;
    } catch (error) {
      console.error('Error generating HTML report:', error);
      return '<div>Error: Failed to generate report</div>';
    }
  };

  const handleWalletAddressChange = (e) => {
    setWalletAddress(e.target.value);
    // Reset any previous results
    setAnalysisError(null);
    setReport(null);
    setShowReport(false);
    setAnalysisComplete(false);
  };

  // Remove the redundant handleSubmit function
  // handleSubmit function is a duplicate of handleAnalyzerSubmit
  
  // Generate a minimal fallback report when full analysis fails
  const generateFallbackReport = (address, error, isRateLimited = false) => {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Basic XRP Wallet Info: ${address}</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            background-color: #f8f9fa;
            margin: 0;
            padding: 20px;
          }
          .report-header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 1px solid #eee;
          }
          .report-header h1 {
            margin-bottom: 5px;
            color: #0077cc;
          }
          .wallet-address {
            color: #666;
            font-family: monospace;
            margin-bottom: 20px;
            word-break: break-all;
          }
          .error-box {
            background-color: #fff3cd;
            border-left: 4px solid #ffc107;
            padding: 15px;
            margin-bottom: 20px;
            color: #856404;
            border-radius: 4px;
          }
          .rate-limit-box {
            background-color: #f8d7da;
            border-left: 4px solid #dc3545;
            padding: 15px;
            margin-bottom: 20px;
            color: #721c24;
            border-radius: 4px;
          }
          .info-card {
            background-color: white;
            border-radius: 8px;
            padding: 20px;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
            margin-bottom: 20px;
          }
          .external-links {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 20px;
          }
          .btn {
            display: inline-block;
            padding: 10px 15px;
            background-color: #0077cc;
            color: white;
            text-decoration: none;
            border-radius: 4px;
            font-weight: bold;
          }
          .tips {
            margin-top: 20px;
            padding: 15px;
            background-color: #e9f5ff;
            border-radius: 4px;
          }
          .tips h3 {
            margin-top: 0;
            color: #0077cc;
          }
          .tips ul {
            margin-bottom: 0;
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <h1>XRP Wallet Information</h1>
          <div class="wallet-address">${address}</div>
        </div>
        
        ${isRateLimited ? 
          `<div class="rate-limit-box">
            <strong>Rate Limit Detected:</strong> The XRPL server is currently experiencing high load and has rate-limited your request.
            <br><br>
            The wallet analyzer fetches transaction history in batches. Rate limiting typically occurs when analyzing wallets with many transactions or during periods of high network activity.
            <br><br>
            <strong>What you can do:</strong>
            <ul>
              <li>Try again during off-peak hours</li>
              <li>View your wallet using the external explorers linked below</li>
              <li>Consider analyzing a wallet with fewer transactions</li>
            </ul>
          </div>` 
          : 
          `<div class="error-box">
            <strong>Note:</strong> Only showing basic information due to an error with the full analysis.
            <br><br>
            <small>Error details: ${error.message}</small>
          </div>`
        }
        
        <div class="info-card">
          <h2>View this wallet on external explorers:</h2>
          <div class="external-links">
            <a href="https://xrpscan.com/account/${address}" target="_blank" class="btn">XRPSCAN</a>
            <a href="https://livenet.xrpl.org/accounts/${address}" target="_blank" class="btn">XRPL Explorer</a>
            <a href="https://xrplorer.com/account/${address}" target="_blank" class="btn">XRPlorer</a>
            <a href="https://bithomp.com/explorer/${address}" target="_blank" class="btn">Bithomp</a>
          </div>
        </div>
        
        <div class="info-card">
          <h2>Common Issues:</h2>
          <ul>
            <li>XRPL network may be experiencing connectivity issues</li>
            <li>The wallet may have a large number of transactions that exceeded our processing limits</li>
            <li>Some XRPL nodes may be under maintenance</li>
          </ul>
          <p>Try again later or use one of the external explorers linked above for more detailed information.</p>
        </div>
        
        <div class="tips">
          <h3>Tips for XRPL Wallet Analysis</h3>
          <ul>
            <li>For wallets with many transactions, the analysis may take longer or be rate-limited</li>
            <li>External explorers often have more server resources and can handle larger transaction histories</li>
            <li>The most recent transactions are analyzed first, so even with rate limiting, you can see recent activity</li>
          </ul>
        </div>
      </body>
      </html>
    `;
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    
    if (!filterText || filterText.trim() === '') {
      return;
    }
    
    setSearchLoading(true);
    setSearchError(null);
    setSpecificTokenData(null);
    setLoading(true); // Add loading state for main token list
    
    const trimmedQuery = filterText.trim();
    console.log('Search query:', trimmedQuery);
    
    try {
      // Set user-friendly message
      setError(`Searching for "${trimmedQuery}"...`);
      
      // Use the XrplToService searchTokens method instead of direct axios call
      const searchResponse = await xrplToService.searchTokens(trimmedQuery);
      
      console.log('Search API response:', searchResponse);
      
      if (searchResponse && 
          searchResponse.result === 'success' && 
          searchResponse.tokens && 
          searchResponse.tokens.length > 0) {
        
        // Show search success metrics in console
        if (searchResponse.took) {
          console.log(`Search completed in ${searchResponse.took}ms`);
        }
        
        // Update the tokens list with search results
        setTokens(searchResponse.tokens);
        setError(null);
      } else {
        // No results found
        setSearchError(`No results found for "${trimmedQuery}"`);
        setError(null);
      }
    } catch (err) {
      console.error('Search API error:', err);
      setSearchError('Error searching for tokens. Please try again.');
      setError(null);
    } finally {
      setSearchLoading(false);
      setLoading(false);
    }
  };
  
  // Create a token detail component to show when a specific token is found
  const SpecificTokenDetail = ({ tokenData }) => {
    if (!tokenData || !tokenData.token) return null;
    
    const token = tokenData.token;
    const displayName = token.name || token.currency || 'Unknown';
    
    return (
      <div style={{
        background: '#f9f9ff', 
        borderRadius: '12px',
        padding: '20px',
        margin: '20px 0',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(63, 140, 255, 0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TokenIcon token={token} fallbackColor={stringToColor(displayName)} size="60px" />
            <div>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '1.6rem' }}>{displayName}</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>{token.currency}</span>
                <span style={{ color: '#888', fontSize: '0.8rem', opacity: 0.7 }}>•</span>
                <a 
                  href={`https://xrpl.to/token/${token.md5 || ''}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#3F8CFF', fontSize: '0.9rem', textDecoration: 'none' }}
                >
                  View on XRPL.to
                </a>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#333' }}>
              ${token.exch ? formatPrice(token.exch) : '---'}
            </div>
            <div style={{ 
              color: token.pro24h > 0 ? '#00c853' : token.pro24h < 0 ? '#ff3d00' : '#666',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              {token.pro24h > 0 ? '+' : ''}{token.pro24h ? `${token.pro24h.toFixed(2)}%` : '---'}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', margin: '20px 0' }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>Market Cap</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
              ${token.marketcap ? formatNumber(token.marketcap) : '---'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>24h Volume</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
              ${token.vol24hxrp ? formatNumber(token.vol24hxrp * tokenData.exch?.USD || 0) : '---'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>Holders</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
              {token.holders ? formatNumber(token.holders) : '---'}
            </div>
          </div>
        </div>
        
        {token.tags && token.tags.length > 0 && (
          <div style={{ margin: '20px 0' }}>
            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>Tags:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {token.tags.map((tag, i) => (
                <span key={i} style={{
                  background: 'rgba(63, 140, 255, 0.1)',
                  color: '#3F8CFF',
                  padding: '4px 10px',
                  borderRadius: '50px',
                  fontSize: '0.8rem'
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '4px' }}>Issuer</div>
            <a 
              href={`https://xrpl.to/account/${token.issuer}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.9rem', color: '#3F8CFF', textDecoration: 'none' }}
            >
              {shortenAddress(token.issuer)} {token.user ? `(${token.user})` : ''}
            </a>
          </div>
          
          {token.verified && (
            <div style={{
              background: 'rgba(0, 200, 83, 0.1)',
              color: '#00c853',
              padding: '4px 12px',
              borderRadius: '50px',
              fontSize: '0.8rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <span style={{ fontSize: '1rem' }}>✓</span> Verified
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // ... existing code ...
  
  // Add the search form in the render section, replacing the FilterInput
  // Find the <FilterInput /> component in the render and replace with:
  <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flex: 1, gap: '8px' }}>
    <FilterInput 
      type="text" 
      placeholder="Search by token name, currency, or issuer..."
      value={filterText}
      onChange={handleFilterChange}
    />
    <button 
      type="submit"
      style={{
        background: theme.primary,
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        padding: '0.5rem 1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {searchLoading ? 'Searching...' : 'Search'}
    </button>
  </form>
  
  // Add search results rendering
  // After the form, add this before the token list:
  {searchError && (
    <div style={{
      padding: '12px 20px',
      margin: '20px 0',
      background: 'rgba(255, 76, 76, 0.1)',
      color: '#ff4c4c',
      borderRadius: '8px',
      fontWeight: '500',
      textAlign: 'center'
    }}>
      {searchError}
    </div>
  )}
  
  {specificTokenData && (
    <SpecificTokenDetail tokenData={specificTokenData} />
  )}
  
  // ... existing code ...

  return (
    <TokenFeedContainer theme={currentTheme}>
      <TokenHeader theme={currentTheme}>
        <TokenTitle>
          Popular Tokens
          {lastRefreshTime && (
            <RefreshIndicator>
              <span className="auto-refresh">Auto-refreshes every 15s</span> • Updated <span>{formatRefreshTime(lastRefreshTime)}</span>
              <RefreshButton 
                onClick={handleManualRefresh}
                title="Refresh token list now"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
              </RefreshButton>
            </RefreshIndicator>
          )}
        </TokenTitle>
        
        <TokenControls>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flex: 1, gap: '8px' }}>
          <FilterInput 
            type="text" 
              placeholder="Search by token name, currency, or issuer..."
            value={filterText}
            onChange={handleFilterChange}
          />
            <button 
              type="submit"
              style={{
                background: theme.primary,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '0.5rem 1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </form>
          {searchError && (
            <div style={{
              padding: '12px 20px',
              margin: '20px 0',
              background: 'rgba(255, 76, 76, 0.1)',
              color: '#ff4c4c',
              borderRadius: '8px',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {searchError}
            </div>
          )}
          {specificTokenData && (
            <SpecificTokenDetail tokenData={specificTokenData} />
          )}
          <NewTokenToggle 
            active={showNewTokens} 
            onClick={handleNewTokensToggle}
          >
            {showNewTokens ? "All Tokens" : "New Tokens"}
          </NewTokenToggle>
          <SortSelect value={sortBy} onChange={handleSortChange}>
            <option value="trendingScore">Trending</option>
            <option value="vol24hxrp">24h Volume</option>
            <option value="marketcap">Market Cap</option>
            <option value="trustlines">Holders</option>
            <option value="name">Name</option>
            <option value="pro24h">24h Change</option>
            <option value="createdAt">Creation Date</option>
          </SortSelect>
          <SortSelect value={sortType} onChange={handleSortTypeChange}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </SortSelect>
          <AnalyzerButton onClick={handleOpenAnalyzer}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            Wallet Analyzer
          </AnalyzerButton>
        </TokenControls>
      </TokenHeader>
      
      <CurrencyTabs>
        <CurrencyTab 
          active={currency === 'xrp'} 
          onClick={() => handleCurrencyChange('xrp')}
        >
          XRP
        </CurrencyTab>
        <CurrencyTab 
          active={currency === 'usd'} 
          onClick={() => handleCurrencyChange('usd')}
        >
          USD
        </CurrencyTab>
      </CurrencyTabs>
      
      {/* Display specific token detail when available */}
      {specificTokenData && (
        <div style={{ 
          padding: '1rem 1.5rem', 
          margin: '0 1.5rem 1.5rem',
          background: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '20px',
          boxShadow: '0 10px 25px rgba(63, 140, 255, 0.15)',
        }}>
          <SpecificTokenDetail tokenData={specificTokenData} />
        </div>
      )}
      
      <TokenList>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {Array.isArray(tokens) && tokens.length > 0 ? (
          tokens.map((token, index) => (
            <TokenCard 
              key={`${token.issuer || ''}_${token.currency || ''}_${index}`} 
              onClick={() => handleTokenClick(token)}
              role="button"
              tabIndex={0}
              aria-label={`View details for ${token.displayName || 'token'}`}
            >
              <TokenInfo>
                <TokenNameSection>
                  <TokenIcon 
                    token={token} 
                    fallbackColor={stringToColor(token.displayName)}
                  />
                  <TokenName>
                    <TokenSymbol>
                      {token.displayName || 'Unknown Token'}
                      {isJustIssued(token.createdAt) && 
                        <JustIssuedBadge>JUST ISSUED {getMinutesAgo(token.createdAt)}</JustIssuedBadge>
                      }
                      {!isJustIssued(token.createdAt) && isNewToken(token.createdAt) && <NewBadge>NEW</NewBadge>}
                      {(token.verified || 
                        token.xpad === true || 
                        (token.domain && (token.domain.includes('xpad.') || token.domain === 'xpad.space')) ||
                        token.created_via === "xpad") && 
                        <VerifiedBadge>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </svg>
                          VERIFIED
                        </VerifiedBadge>
                      }
                    </TokenSymbol>
                    <TokenIssuer>{token.issuer ? token.issuer.substring(0, 16) + '...' : 'Unknown Issuer'}</TokenIssuer>
                    {token.createdAt && (
                      <TokenMeta>Created: {formatDate(token.createdAt)}</TokenMeta>
                    )}
                  </TokenName>
                </TokenNameSection>
                <PercentChange positive={(parseScientificNotation(token.pro24h) >= 0)}>
                  {parseScientificNotation(token.pro24h) >= 0 ? '+' : ''}
                  {parseFloat(token.pro24h).toFixed(2)}%
                </PercentChange>
              </TokenInfo>
              
              <TokenStats>
                <StatItem>
                  <StatLabel>Price</StatLabel>
                  <StatValue>
                    {currency === 'usd' 
                      ? `$${formatPrice(calculateUsd(token.exch))}`
                      : `${formatPrice(token.exch)} XRP`}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Volume (24h)</StatLabel>
                  <StatValue>
                    {currency === 'usd'
                      ? `$${formatNumber(calculateUsd(token.vol24hxrp))}`
                      : `${formatNumber(token.vol24hxrp)} XRP`}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Market Cap</StatLabel>
                  <StatValue title={currency === 'usd' 
                      ? `$${formatNumber(calculateUsd(getMarketCap(token)))}`
                      : `${formatNumber(getMarketCap(token))} XRP`}>
                    {currency === 'usd' 
                      ? `$${formatNumber(calculateUsd(getMarketCap(token)))}`
                      : `${formatNumber(getMarketCap(token))} XRP`}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Holders</StatLabel>
                  <StatValue>
                    {formatNumber(token.trustlines)}
                  </StatValue>
                </StatItem>
              </TokenStats>
              
              <MoreInfoButton onClick={(e) => {
                e.stopPropagation(); // Stop event from bubbling up to the card
                handleTokenClick(token);
              }}>
                View Details ↗
              </MoreInfoButton>
            </TokenCard>
          ))
        ) : !loading ? (
          <ErrorMessage>No tokens found</ErrorMessage>
        ) : null}
        
        {loading && <LoadingSpinner />}
      </TokenList>
      
      {!loading && hasMore && (
        <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
          Load More Tokens
        </LoadMoreButton>
      )}
      
      {/* Wallet Analyzer Modal */}
      {showAnalyzerModal && (
        <AnalyzerModal>
          <ModalContent>
            <ModalHeader>
              <h3>XRP Wallet Analyzer</h3>
              <button onClick={handleCloseAnalyzer}>&times;</button>
            </ModalHeader>
            
            <WalletAnalyzerForm onSubmit={handleAnalyzerSubmit}>
              <WalletInputGroup>
                <WalletAddressInput
                  type="text"
                  placeholder="Enter XRP wallet address (e.g. rTYXXXXXXXXXXXXXXXXXXXXXX)"
                  value={walletAddress}
                  onChange={handleWalletAddressChange}
                  disabled={isAnalyzing}
                />
                <AnalyzeButton 
                  type="submit" 
                  disabled={isAnalyzing || !walletAddress}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Wallet'}
                </AnalyzeButton>
              </WalletInputGroup>
              
              {isAnalyzing && (
                <AnalysisProgressContainer>
                  <LoadingSpinner size="30px" />
                  <ProgressStatus>
                    <strong>{loadingProgress.status || 'Processing wallet data...'}</strong>
                    {loadingProgress.loaded > 0 && (
                      <div>Loaded {loadingProgress.loaded} transactions</div>
                    )}
                  </ProgressStatus>
                </AnalysisProgressContainer>
              )}
              
              {analysisError && (
                <WalletErrorMessage>
                  {analysisError.includes('\n') ? (
                    <>
                      {analysisError.split('\n')[0]}
                      <pre>{analysisError.split('\n').slice(1).join('\n')}</pre>
                    </>
                  ) : (
                    analysisError
                  )}
                  
                  {analysisError.includes('Connection to XRPL network failed') && (
                    <DetailedErrorInfo>
                      <ul>
                        <li>Check your internet connection</li>
                        <li>Try again in a few minutes</li>
                        <li>Use a different wallet address</li>
                        <li>If the problem persists, your network might be blocking WebSocket connections</li>
                      </ul>
                    </DetailedErrorInfo>
                  )}
                </WalletErrorMessage>
              )}
            </WalletAnalyzerForm>
            
            {isAnalyzing && (
              <AnalysisLoadingContainer>
                <AnalysisLoadingSpinner />
                <p>Analyzing wallet transactions... This may take a minute.</p>
                <small>Connecting to the XRP Ledger network...</small>
              </AnalysisLoadingContainer>
            )}
            
            {showReport && report && (
              <ReportContainer>
                <ReportIframe
                  srcDoc={report}
                  title="Wallet Analysis Report"
                  width="100%"
                  height="800"
                  frameBorder="0"
                />
              </ReportContainer>
            )}
          </ModalContent>
        </AnalyzerModal>
      )}
    </TokenFeedContainer>
  );
};

export default TokenFeed;

// Add these styled components for the progress display
const AnalysisProgressContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #0070f3;
`;

const ProgressStatus = styled.div`
  margin-left: 15px;
  font-size: 14px;
  color: #333;
  
  div {
    margin-top: 5px;
    color: #666;
  }
`;

      